/**
 * The attention selector: one pure function feeds the Overview list and
 * the verdict bar in BOTH views (twin slice 1, plan W5 step 10; ideation
 * §7). Flipping Map | Building never adds, drops or rewords an item,
 * because neither view computes one.
 *
 * WHAT IT DOES
 *   - Turns robot reports (registry issue categories, status, unavailable
 *     reason) and W6's lift readings into items, through the copy tables.
 *     A client-derived report appears once the debounce has raised it, or
 *     at once when an open alert already backs it (the server has decided);
 *     an open robot alert no live report accounts for becomes a card of its
 *     own and follows its alert. The server's alert-row rules (a door that
 *     didn't open, a door a robot can't get through, a robot stuck in
 *     one spot; api-server 0.6.42) join the card of the robot they are
 *     about -- for a door, the robot behind the door's last request in its
 *     live state, kept while the alert is open (`doorAlertRobot`) -- or,
 *     for a door no robot can be named for, are the door's own card. A
 *     stalled robot stuck at a door names the door -- the first one this
 *     page found it at, kept while the alert is open (`stallDoor`) -- and
 *     says whether that door has opened for it. A lift alert counts as
 *     placed only once
 *     its car's item is up, and only by an item that asks someone to act
 *     (an Info item shows no evidence line, so no alert); until then it
 *     is one of the "other open alerts", never dropped.
 *   - Groups by who acts: Needs you (a physical step on site), Nexsoss (a
 *     remote fix), Info (shown, nobody asked yet). Picked-up items leave
 *     the Nexsoss group for a collapsed "Picked up" list.
 *   - Orders: anything ranked first; then, in Needs you, safety before
 *     blocked work before upkeep; then a card the server raised to
 *     critical (only an alert-row card can be); in Nexsoss, please-call
 *     before the rest; then items with their own start stamp, oldest
 *     first; then by "seen here since".
 *   - Coalesces: one card per robot (its most urgent report heads it, the
 *     rest are listed); the same report on one known floor, with the same
 *     evidence line, becomes one card -- never a safety or ranked-first
 *     one, nor a row whose card is its own (`ownCard`), whose steps are
 *     that robot's own; a robot only waiting on a
 *     lift that is itself raised folds under the lift; building-wide
 *     causes become site rows that absorb their cars.
 *   - "Looks fixed, confirming…" (amendment A4) and then "Cleared at"
 *     (Decision 9) come from the debounce view, not from the live
 *     reading, which by then no longer shows the condition: the debounce
 *     remembers what each key was about. While the 30 s clear dwell runs
 *     the card stays where it was, dimmed, with no steps, no e-stop card
 *     and no evidence line -- there is nothing to do and nothing to
 *     claim; before A4 it vanished for those 30 s. One card per robot or
 *     car, only where nothing is up for it, never over a condition the
 *     reading shows again (that is simply up again), and never for a car
 *     whose card was never up (a hold that ended before 5 min). A car or
 *     a lift site row that moves straight into ANOTHER condition keeps
 *     its card, headed by what it was, while the new one dwells. The
 *     debounce may lag the reading by a render either way: a key it still
 *     calls up that the reading no longer shows looks fixed from now.
 *   - An unreadable car is not "fixed" (amendment A7). A raised condition
 *     the reading no longer shows because it can't speak for it -- the
 *     car has no reading, is seen only once or is read through a gate that
 *     can't be read or is off; a site row's deciding car can't be read
 *     (or is behind a gate that is off); the robot's presence is lost or
 *     checking; the source did not read; the feed is clamped -- is held up
 *     as it was: its own words, who acts, steps and evidence, over the
 *     lines that say why it can't be read now, until the reading's own
 *     card ("No reading", "Can't tell ...", "Lift control off") takes over
 *     or it reads again. It never looks fixed and never clears from
 *     silence (`robotCannotRead` and `liftCannotRead` tell the debounce
 *     the same), and the verdict never says "All clear" over it. A robot
 *     that can't be read is held so even while its registry still LISTS
 *     the report (A17b): its card, e-stop clock included, is the one it
 *     had when it went dark (A11).
 *   - Gives the verdict. "All clear" is SCOPED -- "Nothing needs you
 *     (robots and lifts)" -- and only when every source read cleanly, the
 *     feed is live, nothing this viewer could see is still looking fixed
 *     (read from the debounce, not from the cards), and no open alert is
 *     left unaccounted for: alerts the list cannot place are counted on
 *     their own row, "N other open alerts".
 *
 * FIRE is passive (the shell's FireAlarmOverlay is the authority). Walk
 * steps are withdrawn, and the fire row says per robot only what the data
 * shows: its last report, and "Held" only with the fire-hold issue. It
 * never says a job was "paused by the alarm": fire mode records no
 * interrupted tasks any more.
 *
 * COPY STATUS: pending Akash's copy pass 2 (plan gate B).
 */
import {
  ALERTS_ROLES,
  audienceFor,
  canSeeSimulated,
  hasAnyRole,
  type Audience,
  type RoleLike,
} from '../../auth/roles';
import type { PresenceKind } from '../../screens/Map/robot-presence';
import { alertFingerprint, alertRule, alertSubject, type AttentionAlert } from './alert-subject';
import type { DebouncedStatus } from './debounce';
import { estopCard, type EstopCard, type EstopJob } from './estop';
import {
  evidenceFor,
  type AlertKey,
  type AlertMuteLike,
  type Evidence,
} from './evidence';
import {
  LIFT_GATE_ALERT_RULE,
  LIFT_UNREAD_KINDS,
  SITE_ROW_WHO_ACTS,
  WHO_FROM_READING,
  liftAlertKeys,
  liftAttention,
  liftCardAfterMs,
  liftConditionKey,
  liftWhoActs,
  siteRowAlertKeys,
  siteRowCars,
  siteRowUnread,
  type LiftReadingInput,
  type LiftRobotRef,
  type LiftSiteRowInput,
  type LiftWhoActs,
} from './lift-items';
import {
  BAND_RANK,
  DOOR_CARD_COPY,
  NEEDS_A_CHECK,
  ROBOT_COPY,
  robotCopyForCategory,
  robotCopyForReason,
  robotCopyRow,
  type Band,
  type DoorCardKey,
  type RobotCopyContext,
  type RobotCopyKey,
  type RobotCopyRow,
  type WhoActs,
} from './robot-copy';
import {
  FIRE_ALARM,
  FIRE_HOLD_WORDS,
  clockText,
  dayClockText,
  joinNames,
  levelWords,
  type SiteTimeZone,
} from './words';

export type SourceHealth = 'ok' | 'loading' | 'failed';

/** One robot, as the attention list needs it. */
export interface AttentionRobotInput {
  fleet: string;
  name: string;
  /** `RobotStateView.status`. */
  status: string;
  unavailableReason?: string | null;
  /** Registry issue categories, verbatim (`RobotCommissionInfo.issues`). */
  issues: readonly string[];
  /** The robot's map name when its position is trusted (W3's presence). */
  level: string | null;
  /** Where it stands on `level`, in the building map's metres, while its
   *  position is trusted, as `level` is; absent or null when not. */
  at?: MapPoint | null;
  simulated: boolean;
  /** For the e-stop card. */
  job: EstopJob;
  /** `heldLift()`'s car, by name. */
  heldLift: string | null;
  /** The robot's last report (worker stamp), for the fire row's "at". */
  lastReportAtMs?: number | null;
  /**
   * W3's presence (`robot-presence.ts`): `lost` (no signal, a roster stub,
   * an unreachable reason, offline) and `checking` (seen once) mean this
   * record can't be read now, so a report it no longer shows has not gone
   * (A7; `robotCannotRead`) -- save the reason a lost robot's roster
   * gives, which still reads. Null: not classified -- a robot only an
   * alert names -- and read as readable, since nothing says it is not.
   */
  presence: PresenceKind | null;
}

/** A point on a floor of the building map, in its metres. */
export interface MapPoint {
  x: number;
  y: number;
}

/** A robot as a door request names it: RMF's requester `<fleet>/<robot>`
 *  split at its last slash, as the engine splits it
 *  (`alerts_engine._door_requesting_robot`). */
export interface DoorRobot {
  fleet: string | null;
  robot: string;
}

/**
 * One door, as the alert-row cards read it (api-server 0.6.42): whether it
 * reads open, the robot behind its last request, and its floor and place.
 * `attentionDoors` builds it from the console's door list and the building
 * map's door registry.
 */
export interface AttentionDoorInput {
  name: string;
  /** Its floor on the building map (`doors_registry`); null when the map
   *  gives none. */
  level: string | null;
  /** Where it stands on that floor: the map's first end of the door
   *  (`doors_registry` v1), in the metres a robot's pose is in; null when
   *  the map gives none. */
  at: MapPoint | null;
  /** Its state reads open (`mode` is `open`), as the engine's rules read
   *  it; false for closed, moving, unknown and no state. */
  open: boolean;
  /**
   * The door's last request (`DoorState.last_command`, worker 0.4.21+)
   * when a ROBOT made it: which robot, OPEN or CLOSE, and when it was first
   * made (RMF repeats a request every second; the worker keeps its first
   * `at_ms`). Null when the last request is a console's or anyone else's,
   * or none has been seen.
   */
  robotRequest: (DoorRobot & { requested: 'open' | 'close'; atMs: number }) | null;
}

/**
 * The robot a door alert is about, by the door's live state: the robot
 * behind its last request.
 *
 * AN OPEN names the robot waiting for the door, as the engine names it
 * (`alerts_engine._door_requesting_robot`). A CLOSE names the robot too:
 * RMF sends it when the robot's leg through the door ends, and neither
 * door rule ends on it. `door_passage_stalled` keeps its row open for
 * 30 s after the robot's own close, waiting for the re-ask a replan makes
 * about 15 s later (`_DOOR_PASSAGE_RETRY_S`; four such cycles at
 * L4_SLL_Door on 2026-09-07), and `door_open_failed` keeps it 10 s
 * (`_DOOR_OPEN_FAILED_WITHDRAWN_S`). Reading only an open moved the alert
 * off the robot's card and onto a door card ("Open for a robot that can't
 * get through") on every cycle, and kept that door card up for 30 s after
 * a slow pass that did get through. A console's or anyone else's request
 * names nobody: the robot that was waiting is the one this page named
 * before (`doorAlertRobot`).
 */
export function doorRequester(door: AttentionDoorInput | undefined): DoorRobot | null {
  const r = door?.robotRequest;
  return r ? { fleet: r.fleet, robot: r.robot } : null;
}

/**
 * The robot a door alert's card is about: the one this page first named
 * for that alert (`remembered`, by alert id: `rememberDoorAlertRobots`,
 * which the hook keeps), else the door's live state's (`doorRequester`).
 *
 * THE FIRST NAME HOLDS while the alert is open. One `door_passage_stalled`
 * row is one robot's episode: another robot asking ends it, and so does a
 * console request, but the row stays on this page until the next alerts
 * poll (15 s), and for that long the door's state names someone else, or
 * no one. A console Close under a `door_open_failed` row lasts about a
 * second before the waiting robot asks again. Neither moves the card.
 */
export function doorAlertRobot(
  alertId: string,
  door: AttentionDoorInput | undefined,
  remembered: ReadonlyMap<string, DoorRobot> | undefined,
): DoorRobot | null {
  return remembered?.get(alertId) ?? doorRequester(door);
}

export interface AttentionInput {
  /** The viewer's roles, either spelling. The audience and whether
   *  simulated robots show both come from `src/auth/roles.ts`. */
  roles: readonly RoleLike[];
  /** This device's wall clock (`Date.now()`): the debounce's reading
   *  times, mute expiry and alert ages are all on it, and the verdict's
   *  times read it to name the day (`dayClockText`). */
  nowMs: number;
  clock: SiteTimeZone;
  robots: { health: SourceHealth; rows: readonly AttentionRobotInput[] };
  lifts: {
    health: SourceHealth;
    readings: readonly LiftReadingInput[];
    siteRow: LiftSiteRowInput | null;
  };
  alerts: {
    /** The open-alerts list read. */
    health: SourceHealth;
    rows: readonly AttentionAlert[];
    /** `GET .../alerts/summary`'s count; null when it could not be read. */
    summaryOpen: number | null;
    /** The list endpoint's page size (`limit`, 100 by default). */
    pageLimit: number;
  };
  /** Null when the mutes list could not be read. */
  mutes: readonly AlertMuteLike[] | null;
  /** W3's FeedHealth: true while every mark reads last-known. */
  feed: { clamped: boolean; lastUpdateAtMs: number | null };
  fireActive: boolean;
  /** A planned maintenance window freezing dispatch, when one is open. */
  maintenanceUntilMs: number | null;
  /** `debouncedView(...)` over both sources' conditions, as fed by
   *  `robotConditionsShown` and `liftConditionsShown`, with
   *  `robotCannotRead` and `liftCannotRead` as each reading's
   *  `cannotRead` (A7). */
  debounce: ReadonlyMap<string, DebouncedStatus<ConditionAbout>>;
  /** Off-by-default server rules that are switched on (W7's, and A14's
   *  `lift_gate_disarmed`). */
  enabledRules?: ReadonlySet<string>;
  /** The site the readings are of. Read only to name the site's lift
   *  write gate, whose alert (`lift_gate_disarmed`, A14) is the "Lift
   *  control off" row's evidence (`siteRowAlertKeys`); absent or empty,
   *  that alert is neither matched nor promised. */
  siteId?: string | null;
  /** The site's doors (`AttentionDoorInput`): what names the robot a door
   *  alert is about, the door's floor, and the door a stalled robot is
   *  waiting at. Absent, or without the door, a door alert is the door's
   *  own card unless `doorAlertRobots` names its robot. */
  doors?: readonly AttentionDoorInput[];
  /** The robot each open door alert was first about on this page, by
   *  alert id (`doorAlertRobot`). Absent, each is read from `doors`
   *  alone. */
  doorAlertRobots?: ReadonlyMap<string, DoorRobot>;
  /** The door each open `robot_stalled` alert was first found stuck at on
   *  this page, by alert id (`stallDoor`). Absent, each is read from
   *  `doors` alone. */
  stallDoors?: ReadonlyMap<string, StallDoor>;
  /** The doors' live state read. Until it has, a stalled robot's card
   *  can't tell whether it waits at a door, so it offers no cancel
   *  (`RobotCopyContext.doorUnknown`). Absent, read as read. */
  doorsHealth?: SourceHealth;
}

export type AttentionGroup = 'needs_you' | 'nexsoss' | 'info';

export type AttentionSubject =
  | { kind: 'robot'; robots: LiftRobotRef[] }
  | { kind: 'lift'; lift: string }
  | { kind: 'site'; row: string }
  /** A door alert no robot can be named for (api-server 0.6.42). */
  | { kind: 'door'; door: string };

export interface AttentionItem {
  key: string;
  subject: AttentionSubject;
  group: AttentionGroup;
  band: Band;
  rankFirst: boolean;
  /**
   * Headed by a report whose alert the server has raised to critical
   * (`door_open_failed` left 10 min): first within its band, as the Alerts
   * list puts a critical first. Only the alert-row rules
   * (`ALERT_ROW_RULES`) set it, so every other card is false and ranks as
   * it always has: the list read no severity before them.
   */
  critical: boolean;
  /** The robot, lift or door name, or the site row's words. */
  title: string;
  /** The copy table's headline, or W6's Robots line for a car. */
  headline: string;
  /** W6's who-acts line for a car (or the car that decides a site row),
   *  verbatim; null for a robot, whose steps say it. */
  whoActsLine: string | null;
  /** Further reports of the same robot, by headline. */
  also: string[];
  /** W6's Guests and Robots lines for a car; empty otherwise. */
  lines: string[];
  steps: string[];
  /** "On site:" over an ops reader's steps. */
  stepsHeading: string | null;
  estop: EstopCard | null;
  level: string | null;
  /** A start stamp of the item's own (alert `openedAtMs`, F1), or null. */
  sinceMs: number | null;
  sinceSource: 'alert' | 'lift' | null;
  /** When this page first saw it (device clock); null for alert-backed. */
  seenSinceMs: number | null;
  /** Does Nexsoss know? Null where the question does not arise: a
   *  clearing or cleared item, an item nobody is asked to act on (Info),
   *  and the passive fire and maintenance rows. */
  evidence: Evidence | null;
  /** Up; looking fixed while the 30 s clear dwell runs (A4); or cleared
   *  (Decision 9). Only `active` asks anything of anyone. */
  status: 'active' | 'clearing' | 'cleared';
  /** "Looks fixed, confirming…" or "Cleared at HH:MM"; null while up. */
  statusLine: string | null;
  /** The first reading that no longer showed it, while clearing. */
  lookedFixedAtMs: number | null;
  clearedAtMs: number | null;
  /** Items folded under this one, each with its stated join. */
  folded: Array<{ item: AttentionItem; join: string }>;
  /** Ops only: the raw report (category, code), never shown to a site
   *  audience. */
  details: string[];
  copyKey: string;
  /** The device wall clock this item was selected at (`input.nowMs`):
   *  read only to name the day of its times (`cardMetaLine`). */
  wallNowMs: number;
}

export type VerdictKind =
  | 'fire'
  | 'reconnecting'
  | 'checking'
  | 'cant_check'
  | 'items'
  | 'other_alerts'
  | 'clearing'
  | 'all_clear';

export interface AttentionModel {
  verdict: { kind: VerdictKind; text: string; cantCheck: string[] };
  siteRows: AttentionItem[];
  needsYou: AttentionItem[];
  nexsoss: AttentionItem[];
  pickedUp: AttentionItem[];
  info: AttentionItem[];
  otherOpenAlerts: { count: number; text: string; linkToAlerts: boolean } | null;
}

export const ALL_CLEAR = 'Nothing needs you (robots and lifts)';
/** A card, and the verdict, while the clear dwell runs (A4). */
export const LOOKS_FIXED = 'Looks fixed, confirming…';
export const clearedAtText = (clearedAtMs: number, clock: SiteTimeZone): string =>
  `Cleared at ${clockText(clearedAtMs, clock)}`;
/**
 * How a held lift card or site row heads (A7; copy deck v2, N5): with what
 * it last showed, said as last known -- never in the present tense over
 * the line that says it can't be read now ("Robots can't change floors"
 * over "Can't tell whether robots can change floors ...", "Kept out since
 * 14:02" over "No reading").
 */
export const lastKnownText = (text: string): string => `Last known: ${text}`;

/** The "Lift control off" row's who-acts line while a planned maintenance
 *  window is open (copy deck v2, N4; v3, NEW-1). The page knows a window is
 *  open and lift control is off -- not that one caused the other, and a
 *  hotel supervisor can book a window (A22) -- so it says the stand-down
 *  may be the maintenance's and asks for a call only if it is not. */
export const LIFT_CONTROL_OFF_IN_MAINTENANCE =
  'Lift control may have been switched off for the planned maintenance. If it wasn’t, call Nexsoss.';
/** The fire row and verdict: the fire toast's own words (`words.ts`). */
export const FIRE_VERDICT = `${FIRE_ALARM}. ${FIRE_HOLD_WORDS}.`;

const ROBOT_ALERT_RULES: ReadonlySet<string> = new Set([
  'robot_issue',
  'robot_error',
  'robot_unreachable',
  'task_attention',
  'task_long_running',
]);
/** Reports that say nothing of their own about why the robot waits, so
 *  they fold under a raised car the robot is waiting on. */
const FOLDS_UNDER_A_LIFT: ReadonlySet<string> = new Set([
  'stuck_task',
  'stuck_task_lift_rule',
  'robot_error',
]);

/**
 * The server's rules a card is worded from the alert row alone
 * (api-server 0.6.42, 2026-10-21): no robot report or lift reading stands
 * behind them, so each card follows its alert, up while it is open. Each
 * is its copy row's own key. The two door rules are about the DOOR; their
 * card is that of the robot asking for it, named from the door's live
 * state, else the door's own (`DOOR_CARD_COPY`).
 */
export type AlertRowRule = Extract<RobotCopyKey, 'door_open_failed' | 'door_passage_stalled' | 'robot_stalled'>;
export const ALERT_ROW_RULES: ReadonlySet<AlertRowRule> = new Set<AlertRowRule>([
  'door_open_failed',
  'door_passage_stalled',
  'robot_stalled',
]);

/** The copy rows whose card is never shared with another robot
 *  (`RobotCopyRow.ownCard`, `coalescible`). */
const OWN_CARD_ROWS: ReadonlySet<string> = new Set(ROBOT_COPY.filter((r) => r.ownCard).map((r) => r.key));

const robotId = (r: { fleet: string; name: string }) => `${r.fleet}/${r.name}`;

// ---------------------------------------------------------------------------
// Conditions: what the debounce is fed
// ---------------------------------------------------------------------------

interface RobotCondition {
  key: string;
  robot: AttentionRobotInput;
  row: RobotCopyRow;
  arg: string | null;
  /** The raw report, for ops details. */
  raw: string;
  /** Present for an issue category: the alert's exact fingerprint. */
  fingerprint: string | null;
  alertKeys: AlertKey[];
  /** The floor the report itself names, where that is not the robot's
   *  own: a door's, from the building map. Its words name it. */
  floor?: string | null;
  /** An alert-row report whose alert the server raised to critical. */
  critical?: boolean;
  /** `robot_stalled` at a door that has opened for it (`StallDoor`). */
  doorOpened?: boolean;
  /** `robot_stalled` at no door named, while whether it waits at one
   *  can't be told (`StallDoorReading`). */
  doorUnknown?: boolean;
}

/** A report's words on its robot's card: a report about a door names the
 *  door's floor, any other the robot's (`level`). */
function wordsOf(c: RobotCondition, robot: string, level: string | null): RobotCopyContext {
  return {
    robot,
    arg: c.arg,
    level: c.floor ?? level,
    doorOpened: c.doorOpened,
    doorUnknown: c.doorUnknown,
  };
}

/** The reason the worker gives for the robot now (its roster's
 *  `unavailable_reason`, or its own `pending-localize`), as a copy row;
 *  null when it gives none the copy table knows. */
function reasonRowFor(robot: Pick<AttentionRobotInput, 'unavailableReason' | 'status'>): RobotCopyRow | null {
  return (
    robotCopyForReason(robot.unavailableReason) ??
    (robot.status === 'pending-localize' ? robotCopyRow('not_localized') : null)
  );
}

function robotConditionsFor(robot: AttentionRobotInput): RobotCondition[] {
  const out: RobotCondition[] = [];
  const id = robotId(robot);
  const robotKey = (rule: string): AlertKey => ({ rule, sourceKind: 'robot', sourceId: robot.name });
  for (const category of robot.issues) {
    const { row, arg } = robotCopyForCategory(category);
    out.push({
      key: `robot:${id}:issue:${category}`,
      robot,
      row,
      arg,
      raw: category,
      fingerprint: `robot_issue:${robot.name}:${category}`,
      alertKeys: [robotKey('robot_issue')],
    });
  }
  const reasonRow = reasonRowFor(robot);
  if (reasonRow) {
    out.push({
      key: `robot:${id}:reason:${reasonRow.key}`,
      robot,
      row: reasonRow,
      arg: null,
      raw: robot.unavailableReason || robot.status,
      fingerprint: null,
      alertKeys: reasonRow.key === 'state_channel_stale' ? [robotKey('robot_unreachable')] : [],
    });
  }
  // A bare error: the status says error and no report names why.
  if (robot.status === 'error' && robot.issues.length === 0) {
    out.push({
      key: `robot:${id}:robot_error`,
      robot,
      row: robotCopyRow('robot_error'),
      arg: null,
      raw: 'status: error',
      fingerprint: null,
      alertKeys: [robotKey('robot_error')],
    });
  }
  return out;
}

/** The robots and reports this viewer is shown at all: no simulated
 *  robot and no ops-only report for a site audience. */
function shownConditions(
  robots: readonly AttentionRobotInput[],
  roles: readonly RoleLike[],
): { robot: AttentionRobotInput; conditions: RobotCondition[] }[] {
  const audience = audienceFor(roles);
  const simulated = canSeeSimulated(roles);
  return robots
    .filter((r) => simulated || !r.simulated)
    .map((robot) => ({
      robot,
      conditions: robotConditionsFor(robot).filter((c) => audience === 'ops' || !c.row.opsOnly),
    }));
}

/**
 * What a debounce key is about: enough to word its "Cleared at" card once
 * the live reading no longer shows it (Decision 9). The debounce keeps the
 * last one a reading handed over and returns it in its view.
 */
export type ConditionAbout =
  | {
      kind: 'robot';
      /** The robot as the last reading that showed it reported it. */
      robot: AttentionRobotInput;
      copy: RobotCopyKey;
      arg: string | null;
      raw: string;
    }
  | {
      kind: 'lift';
      lift: string;
      reading: LiftReadingInput['kind'];
      /** W6's who acts then: where the card sat while it looks fixed. */
      whoActs: LiftWhoActs;
      /** W6's Robots line then: what a card that looks fixed or has
       *  cleared says it WAS, as a robot's card does. */
      robots: string;
      /** The whole reading then: the card a car that can't be read now
       *  keeps, worded as it last read (A7). */
      last: LiftReadingInput;
    }
  | {
      kind: 'site';
      row: LiftSiteRowInput;
      /** Who acted on the row then (`siteRowWhoActs`): where it sat while
       *  it looks fixed, or while what decides it can't be read (A7). */
      whoActs: WhoActs;
      /** The deciding car's who-acts line then (W6's words); null when
       *  no robot car decided. A row held up while it can't be read keeps
       *  it (A7). */
      whoActsLine: string | null;
      /** What the server would raise for its cars' readings then: the
       *  evidence a held row keeps (A7), never what the cars' readings
       *  NOW -- which say only why they can't be read -- would raise. */
      alertKeys: AlertKey[];
    };

/** The conditions this robot reading shows, by key, for
 *  `advance({source: 'robots', active})`. */
export function robotConditionsShown(
  robots: readonly AttentionRobotInput[],
  roles: readonly RoleLike[],
): Map<string, ConditionAbout> {
  const out = new Map<string, ConditionAbout>();
  for (const { conditions } of shownConditions(robots, roles)) {
    for (const c of conditions) {
      out.set(c.key, { kind: 'robot', robot: c.robot, copy: c.row.key, arg: c.arg, raw: c.raw });
    }
  }
  return out;
}

/**
 * Who acts on a lift site row as its cars' readings say, for the row's
 * `about`: the most urgent robot car it speaks for, by W6's who acts; the
 * row's own default only when it speaks for no robot car. A guest car
 * never decides (Decision 3). The row while UP is decided in the selector
 * the same way, except that a car with a card up speaks through its card
 * -- W5's own timing included, which this reading alone cannot know (a
 * hall wait asks for a call at 10 min) -- so a row that looks fixed keeps
 * the group the readings gave it, which differs from the one it had up
 * only for a car past that 10 min.
 */
function siteRowWhoActs(
  row: LiftSiteRowInput,
  readings: readonly LiftReadingInput[],
): { whoActs: WhoActs; whoActsLine: string | null } {
  // Stable: on a tie, the first car's line.
  const decider = siteRowCars(row, readings)
    .filter((c) => c.robotUse !== 'none')
    .map((c) => ({ whoActs: WHO_FROM_READING[c.whoActs], whoActsLine: c.whoActsLine }))
    .sort((a, b) => WHO_RANK[a.whoActs] - WHO_RANK[b.whoActs])[0];
  return decider ?? { whoActs: SITE_ROW_WHO_ACTS[row.kind], whoActsLine: null };
}

/** The conditions this lift reading shows, by key: what each lift
 *  reading's `advance` takes its part of (one car's, the site row's, the
 *  silence's; `attention-readings.ts`, A19). */
export function liftConditionsShown(
  readings: readonly LiftReadingInput[],
  siteRow: LiftSiteRowInput | null,
): Map<string, ConditionAbout> {
  const out = new Map<string, ConditionAbout>();
  for (const r of readings) {
    const k = liftConditionKey(r);
    if (k) out.set(k, { kind: 'lift', lift: r.name, reading: r.kind, whoActs: r.whoActs, robots: r.robots, last: r });
  }
  if (siteRow && siteRow.kind !== 'fire') {
    out.set(`site:lift:${siteRow.kind}`, {
      kind: 'site',
      row: siteRow,
      ...siteRowWhoActs(siteRow, readings),
      alertKeys: siteRowCars(siteRow, readings).flatMap(liftAlertKeys),
    });
  }
  return out;
}

/** W3's presence kinds whose record can't be read now (A7). */
const UNREAD_PRESENCE: ReadonlySet<PresenceKind> = new Set<PresenceKind>(['lost', 'checking']);

type FeedClamp = Pick<AttentionInput['feed'], 'clamped'>;

/**
 * The robot keys this robot reading cannot speak for (amendment A7), for
 * `advance({source: 'robots', cannotRead})` and the selector alike: every
 * robot key when the robots did not read cleanly or the feed is clamped,
 * else those of a robot whose presence is lost or still checking. Its
 * silence is not evidence of a fix. A robot that has left the list is NOT
 * unread: the worker keeps a roster stub (presence lost) for every robot
 * it still knows, so leaving the list is the fleet dropping it, and a
 * card held for it would never come down.
 *
 * WHAT A LOST ROBOT'S ROSTER STILL SAYS. A robot lost by the reason the
 * worker gives (`edge_offline`, `not_reporting`, `state_channel_stale`)
 * says nothing of its own, but the worker's roster still speaks for its
 * REASON: a reason that changes is a new reading, and the old one looks
 * fixed, then clears. Only its own reports (an issue, a bare error) are
 * held. Except: `edge_offline` -- the site box silent -- cannot say that
 * what the box last said about the robot (`not_reporting`) has gone. A
 * robot lost with no reason the copy table knows (its receipts stopped, a
 * sentinel pose) or still checking (seen once: it could be a replay) has
 * no roster word, so every key of it is held.
 */
export function robotCannotRead(
  robots: Pick<AttentionInput['robots'], 'health' | 'rows'>,
  feed: FeedClamp,
): (key: string) => boolean {
  if (robots.health !== 'ok' || feed.clamped) return (key) => key.startsWith('robot:');
  const unread = new Map<string, (rest: string) => boolean>();
  for (const r of robots.rows) {
    if (r.presence === null || !UNREAD_PRESENCE.has(r.presence)) continue;
    const roster = r.presence === 'lost' ? (reasonRowFor(r)?.key ?? null) : null;
    unread.set(`robot:${robotId(r)}:`, (rest) => {
      if (roster === null || !rest.startsWith('reason:')) return true;
      return roster === 'edge_offline' && rest === 'reason:not_reporting';
    });
  }
  return (key) => {
    for (const [prefix, held] of unread) if (key.startsWith(prefix)) return held(key.slice(prefix.length));
    return false;
  };
}

/**
 * What `robotCannotRead` reads of the rows, as a key: the same key gives
 * the same answer for every key, for the same health and clamp. Its
 * presence moves on the clock, so the hook folds a GLANCE when this
 * changes with no new reading (A17b, `glance`): a robot going dark is
 * dated at the flip, not at the next poll.
 */
export function robotUnreadKey(rows: readonly AttentionRobotInput[]): string {
  return rows
    .filter((r) => r.presence !== null && UNREAD_PRESENCE.has(r.presence))
    .map((r) => `${robotId(r)} ${r.presence} ${r.presence === 'lost' ? (reasonRowFor(r)?.key ?? '') : ''}`)
    .sort()
    .join('\n');
}

/**
 * The lift keys this lift reading cannot speak for (amendment A7), for
 * every lift reading's `advance({cannotRead})` and the selector alike: every
 * lift key when the lifts did not read cleanly or the feed is clamped;
 * else a car's keys while its reading is one of `LIFT_UNREAD_KINDS`, and
 * a site row's while W6's row now says what decides it can't be read
 * (`siteRowUnread`). A car with no reading in the list at all is not
 * unread: the hook reads every car of the site's asset, so a car missing
 * from it is not a car any more.
 */
export function liftCannotRead(
  lifts: Pick<AttentionInput['lifts'], 'health' | 'readings' | 'siteRow'>,
  feed: FeedClamp,
): (key: string) => boolean {
  if (lifts.health !== 'ok' || feed.clamped) {
    return (key) => key.startsWith('lift:') || key.startsWith('site:lift:');
  }
  const cars = lifts.readings.filter((r) => LIFT_UNREAD_KINDS.has(r.kind)).map((r) => `lift:${r.name}:`);
  const now = lifts.siteRow?.kind ?? null;
  return (key) => {
    if (key.startsWith('site:lift:')) {
      return siteRowUnread(key.slice('site:lift:'.length) as LiftSiteRowInput['kind'], now);
    }
    return cars.some((p) => key.startsWith(p));
  };
}

// ---------------------------------------------------------------------------
// The selector
// ---------------------------------------------------------------------------

/**
 * A key's view for a condition the LIVE reading shows: raised, whether the
 * debounce says up or (one render behind the reading) looking fixed.
 * Null when it has not raised.
 */
function raisedView(
  view: DebouncedStatus<ConditionAbout> | undefined,
): { seenSinceMs: number } | null {
  return view?.status === 'active' || view?.status === 'clearing' ? { seenSinceMs: view.seenSinceMs } : null;
}

type QuietState = Extract<ItemState, { status: 'clearing' | 'cleared' }>;

/** Looking fixed outranks a clear: it is nearer to being up. */
const QUIET_RANK: Readonly<Record<QuietState['status'], number>> = { clearing: 1, cleared: 0 };

type Debounce = AttentionInput['debounce'];
type Source = 'robots' | 'lifts';

/** The keys the LIVE reading shows, per source, whoever is looking: a
 *  report hidden from this viewer is still showing, not fixed. */
type LiveKeys = Readonly<Record<Source, ReadonlySet<string>>>;

/** The source whose readings decide a key: what it is about, else its
 *  prefix. */
function sourceOf(key: string, about: ConditionAbout | undefined): Source | null {
  if (about) return about.kind === 'robot' ? 'robots' : 'lifts';
  if (key.startsWith('robot:')) return 'robots';
  if (key.startsWith('lift:') || key.startsWith('site:lift:')) return 'lifts';
  return null;
}

/** The keys each source's live reading cannot speak for (A7). */
type Unread = Readonly<Record<Source, (key: string) => boolean>>;

/**
 * The debounce view, read against the live reading. The hook advances the
 * debounce from the same readings the selector reads, but nothing makes it
 * fold in THIS render's reading first, so it may be one render behind in
 * either direction:
 *   - a key that looks fixed while the reading shows it again is up
 *     (`raisedView`);
 *   - a key still UP while the reading no longer shows it looks fixed from
 *     now -- what the debounce will say once it has seen this reading.
 *     Read as up, it would draw nothing (the reading has no condition to
 *     word a card from) and the verdict would read "All clear" (A4).
 * Only where the reading can speak for the key (A7): a failed or clamped
 * source, a car with no reading, a robot whose presence is lost -- each
 * is silence, never evidence that anything is fixed. Such a key is held
 * up instead, as the debounce will hold it once it sees this reading: one
 * that was looking fixed is up again, and one that was up says it could
 * not be read since now.
 */
function againstReading(input: AttentionInput, live: LiveKeys, unread: Unread): Debounce {
  let out: Map<string, DebouncedStatus<ConditionAbout>> | null = null;
  for (const [key, view] of input.debounce) {
    if (view.status === 'cleared') continue;
    const source = sourceOf(key, view.about);
    if (source === null || live[source].has(key)) continue;
    const about = view.about === undefined ? {} : { about: view.about };
    if (unread[source](key)) {
      if (view.status === 'active' && view.unreadSinceMs !== undefined) continue;
      out ??= new Map(input.debounce);
      out.set(key, { status: 'active', seenSinceMs: view.seenSinceMs, unreadSinceMs: input.nowMs, ...about });
      continue;
    }
    if (view.status !== 'active') continue;
    out ??= new Map(input.debounce);
    out.set(key, { status: 'clearing', seenSinceMs: view.seenSinceMs, lookedFixedAtMs: input.nowMs, ...about });
  }
  return out ?? input.debounce;
}

type RobotAbout = Extract<ConditionAbout, { kind: 'robot' }>;
type LiftAbout = Extract<ConditionAbout, { kind: 'lift' }>;
type SiteAbout = Extract<ConditionAbout, { kind: 'site' }>;

/** A robot report held up while the robot can't be read (A7), as the
 *  reading that last showed it reported it: the same copy, alert keys
 *  and fingerprint. */
function heldRobotCondition(key: string, about: RobotAbout): RobotCondition {
  const same = robotConditionsFor(about.robot).find((c) => c.key === key);
  return (
    same ?? {
      key,
      robot: about.robot,
      row: robotCopyRow(about.copy),
      arg: about.arg,
      raw: about.raw,
      fingerprint: null,
      alertKeys: [],
    }
  );
}

/** The lift site row held up while what decides it can't be read (A7),
 *  the latest raised; null when there is none. */
function heldSiteView(
  debounce: Debounce,
  live: ReadonlySet<string>,
  cannotRead: (key: string) => boolean,
): { was: SiteAbout; seenSinceMs: number } | null {
  let best: { was: SiteAbout; seenSinceMs: number } | null = null;
  for (const [key, v] of debounce) {
    if (v.status !== 'active' || v.about?.kind !== 'site' || live.has(key) || !cannotRead(key)) continue;
    if (!best || v.seenSinceMs > best.seenSinceMs) best = { was: v.about, seenSinceMs: v.seenSinceMs };
  }
  return best;
}

/** A robot report this viewer would have been shown live. */
function robotVisible(roles: readonly RoleLike[], audience: Audience, about: Extract<ConditionAbout, { kind: 'robot' }>) {
  return (!about.robot.simulated || canSeeSimulated(roles)) && (audience === 'ops' || !robotCopyRow(about.copy).opsOnly);
}

/** A car's condition had a card up before it stopped showing: it was seen
 *  for its card's threshold (a hold is carded only at 5 min). */
function liftWasCarded(
  about: Extract<ConditionAbout, { kind: 'lift' }>,
  view: Extract<DebouncedStatus, { status: 'clearing' | 'cleared' }>,
): boolean {
  const end = view.status === 'clearing' ? view.lookedFixedAtMs : view.clearedAtMs;
  const after = liftCardAfterMs(about.reading);
  return after !== null && end - view.seenSinceMs >= after;
}

/**
 * Is anything this viewer could see looking fixed? Read from the debounce
 * itself, not from the cards drawn: "All clear" is never given while a
 * condition is still in its clear dwell, whether or not a card shows it
 * (A4). A key the live reading shows again is up, not looking fixed; a
 * view that does not say what it was about counts, since nothing says the
 * viewer could not have seen it.
 */
function anyLooksFixed(input: AttentionInput, audience: Audience, debounce: Debounce, live: LiveKeys): boolean {
  for (const [key, view] of debounce) {
    if (view.status !== 'clearing') continue;
    const source = sourceOf(key, view.about);
    if (source !== null && live[source].has(key)) continue;
    const about = view.about;
    if (about === undefined) return true;
    if (about.kind === 'robot' && !robotVisible(input.roles, audience, about)) continue;
    if (about.kind === 'lift' && !liftWasCarded(about, view)) continue;
    return true;
  }
  return false;
}

/** The lift site row that looks fixed (the latest), else the latest that
 *  cleared; null when there is neither. Never one the live reading shows
 *  again: that is up, or about to be. */
function quietSiteView(
  debounce: Debounce,
  live: ReadonlySet<string>,
): { was: Extract<ConditionAbout, { kind: 'site' }>; state: QuietState } | null {
  let best: { was: Extract<ConditionAbout, { kind: 'site' }>; state: QuietState; at: number } | null = null;
  for (const [key, v] of debounce) {
    if (v.status === 'active' || v.about?.kind !== 'site' || live.has(key)) continue;
    const state: QuietState =
      v.status === 'clearing'
        ? { status: 'clearing', seenSinceMs: v.seenSinceMs, lookedFixedAtMs: v.lookedFixedAtMs }
        : { status: 'cleared', clearedAtMs: v.clearedAtMs };
    const at = v.status === 'clearing' ? v.lookedFixedAtMs : v.clearedAtMs;
    const rank = (s: QuietState) => QUIET_RANK[s.status];
    if (!best || rank(state) > rank(best.state) || (rank(state) === rank(best.state) && at > best.at)) {
      best = { was: v.about, state, at };
    }
  }
  return best && { was: best.was, state: best.state };
}

const GROUP_OF: Readonly<Record<WhoActs, AttentionGroup>> = {
  you: 'needs_you',
  nexsoss: 'nexsoss',
  none: 'info',
};
const WHO_OF_GROUP: Readonly<Record<AttentionGroup, WhoActs>> = {
  needs_you: 'you',
  nexsoss: 'nexsoss',
  info: 'none',
};
const WHO_RANK: Readonly<Record<WhoActs, number>> = { you: 0, nexsoss: 1, none: 2 };

interface Ctx {
  input: AttentionInput;
  audience: Audience;
  alertsOk: boolean;
  truncated: boolean;
  /** Open alert ids an item accounts for (attached, or hidden on purpose). */
  mapped: Set<string>;
}

function evidence(
  ctx: Ctx,
  keys: readonly AlertKey[],
  fingerprints: readonly string[] | undefined,
  seenSinceMs: number | null,
  asksNoCall = false,
): Evidence {
  const ev = evidenceFor({
    keys,
    fingerprints,
    alerts: { ok: ctx.alertsOk, rows: ctx.input.alerts.rows, truncated: ctx.truncated },
    mutes: ctx.input.mutes,
    seenSinceMs,
    nowMs: ctx.input.nowMs,
    audience: ctx.audience,
    clock: ctx.input.clock,
    enabledRules: rulesThatCanRaiseNow(ctx.input),
    asksNoCall,
  });
  if (ev.alertId) ctx.mapped.add(ev.alertId);
  return ev;
}

/**
 * The off-by-default rules switched on that the server would raise NOW:
 * the caller's, less `lift_gate_disarmed` while a planned maintenance
 * window is open -- the engine mutes it then (A14), so "Nexsoss is told
 * automatically" would be false. An alert already open is still found:
 * this decides only what is promised.
 */
function rulesThatCanRaiseNow(input: AttentionInput): ReadonlySet<string> | undefined {
  const on = input.enabledRules;
  if (!on || input.maintenanceUntilMs === null || !on.has(LIFT_GATE_ALERT_RULE)) return on;
  return new Set([...on].filter((rule) => rule !== LIFT_GATE_ALERT_RULE));
}

/** Every open alert about these keys counts as accounted for, not only
 *  the one the evidence line names. */
function claimAlerts(ctx: Ctx, keys: readonly AlertKey[]): void {
  for (const a of ctx.input.alerts.rows) {
    const rule = alertRule(a);
    const source = a.sourceId || a.source;
    if (keys.some((k) => k.rule === rule && k.sourceId === source)) ctx.mapped.add(a.id);
  }
}

/**
 * The evidence line of an item that asks someone to act, which places
 * every open alert about it; null for one that asks nobody (Info).
 * Ideation §7 gives the line to the items Nexsoss acts on, and with no
 * alert it reads "Nexsoss may not know. Please call." -- beside "Nobody:
 * the fire panel has it" or "Nobody yet" that contradicts the card and
 * asks for the call it says nobody needs. An item with no line shows no
 * alert, so it places none: an open one is counted among "other open
 * alerts", and the verdict is never "All clear" over it.
 */
function evidenceIfAsked(
  ctx: Ctx,
  whoActs: WhoActs,
  keys: readonly AlertKey[],
  seenSinceMs: number | null,
): Evidence | null {
  if (whoActs === 'none') return null;
  claimAlerts(ctx, keys);
  return evidence(ctx, keys, undefined, seenSinceMs);
}

/** The open alerts that back this condition: its rule and robot, and its
 *  exact fingerprint when it names a category. */
function backingAlerts(ctx: Ctx, c: RobotCondition): AttentionAlert[] {
  if (!ctx.alertsOk) return [];
  return ctx.input.alerts.rows.filter((a) => {
    const rule = alertRule(a);
    const source = a.sourceId || a.source;
    if (!c.alertKeys.some((k) => k.rule === rule && k.sourceId === source)) return false;
    return c.fingerprint === null || alertFingerprint(a) === c.fingerprint;
  });
}

function stepsFor(ctx: Ctx, steps: string[]): { steps: string[]; stepsHeading: string | null } {
  // Fire withdraws every walk step (ideation §7).
  if (ctx.input.fireActive) return { steps: [], stepsHeading: null };
  return {
    steps,
    stepsHeading: ctx.audience === 'ops' && steps.length > 0 ? 'On site:' : null,
  };
}

/** W6's who-acts line, unless it sends the person on site somewhere while
 *  fire has withdrawn every walk step. */
function whoActsLineFor(ctx: Ctx, line: string | null, whoActs: WhoActs): string | null {
  return ctx.input.fireActive && whoActs === 'you' ? null : line;
}

/** An item is on screen now (seen here since, if this page saw it), looks
 *  fixed while the clear dwell runs (A4), or shows "Cleared at" (Decision
 *  9). */
type ItemState =
  | { status: 'active'; seenSinceMs: number | null }
  | { status: 'clearing'; seenSinceMs: number; lookedFixedAtMs: number }
  | { status: 'cleared'; clearedAtMs: number };

/** The status fields of an item in this state. */
function statusFields(
  ctx: Ctx,
  state: ItemState,
): Pick<AttentionItem, 'status' | 'statusLine' | 'lookedFixedAtMs' | 'clearedAtMs' | 'seenSinceMs'> {
  switch (state.status) {
    case 'active':
      return { status: 'active', statusLine: null, lookedFixedAtMs: null, clearedAtMs: null, seenSinceMs: state.seenSinceMs };
    case 'clearing':
      return {
        status: 'clearing',
        statusLine: LOOKS_FIXED,
        lookedFixedAtMs: state.lookedFixedAtMs,
        clearedAtMs: null,
        seenSinceMs: state.seenSinceMs,
      };
    case 'cleared':
      return {
        status: 'cleared',
        statusLine: clearedAtText(state.clearedAtMs, ctx.input.clock),
        lookedFixedAtMs: null,
        clearedAtMs: state.clearedAtMs,
        seenSinceMs: null,
      };
  }
}

/**
 * The one line a held robot card carries (A7; copy deck v2, N3): its
 * headline, steps and e-stop card are as it last read (A11), so the card
 * says it can't read the robot now -- as a held car card's W6 lines do
 * ("No reading since 14:40") -- rather than pass the last reading off as
 * now. The same words whatever the reason (presence lost or checking, the
 * robots not read, the feed clamped): each of them is "can't read it now".
 */
export const robotCantReadNowText = (robot: string): string =>
  `Can't read ${robot} now. This card shows it as it last read.`;

/** A robot card's reports held up while the robot can't be read (A7):
 *  since when each could not be read, by key, and the floor the live
 *  record gives now (W3's; null when it has withdrawn it, or when the
 *  robot is not listed). */
interface HeldReports {
  unreadSinceMs: ReadonlyMap<string, number>;
  levelNow: string | null;
}

/**
 * The floor a robot's card may name. Inside a car the robot's own pose
 * still names the floor it BOARDED on (robot-presence), so "can't get out
 * on Level 9" from it would send someone to the wrong landing. A robot is
 * in a car when its own report says so (`EXIT_BLOCKED: <car>`) or W3 reads
 * it riding one (`heldLift` names which): it is then on the CAR's floor,
 * as that car's reading reports it (`carLevel`), and on no floor at all
 * when that is not established -- the car moving, its reading not fresh,
 * the hook not saying, or the car not named. Any other robot: `level`,
 * the floor its live record gives.
 */
function robotFloor(
  ctx: Ctx,
  robot: AttentionRobotInput,
  conditions: readonly RobotCondition[],
  level: string | null,
): string | null {
  const exit = conditions.find((c) => c.row.key === 'exit_blocked');
  const riding = robot.presence === 'riding';
  if (!exit && !riding) return level;
  const car = exit ? exit.arg : robot.heldLift;
  if (!car) return null;
  const reading = ctx.input.lifts.readings.find((r) => r.name === car);
  if (!reading || LIFT_UNREAD_KINDS.has(reading.kind)) return null;
  return reading.carLevel ?? null;
}

/**
 * The car the e-stop card must treat the robot as in or at: W3's
 * `heldLift()`, else the car the robot's own report says it is inside
 * (`EXIT_BLOCKED: <car>`, the in-car rule `robotFloor` applies). Without
 * the second, a card read "Also: Inside SL1 and can't get out" above
 * "Twist the red button to release it." Null when neither says so.
 */
/** The car the fleet adapter handed back to guests after this robot's
 *  e-stop held it (0.7.19), and whether it must be put back on the map. */
function estopHandedBack(
  conditions: readonly RobotCondition[],
): { lift: string; relocalize: boolean } | null {
  const lift = conditions.find((c) => c.row.key === 'lift_handed_back')?.arg;
  if (!lift) return null;
  return { lift, relocalize: conditions.some((c) => c.row.key === 'relocalize_after_lift') };
}

function estopCar(robot: AttentionRobotInput, conditions: readonly RobotCondition[]): string | null {
  if (robot.heldLift) return robot.heldLift;
  // The report always names its car: the copy row matches
  // `EXIT_BLOCKED: <car>` only with something after the colon.
  return conditions.find((c) => c.row.key === 'exit_blocked')?.arg ?? null;
}

/**
 * The alerts that would mean the server has raised this report: its own
 * keys, and for a robot whose site box went quiet (`edge_offline`) the
 * box's ROBOT service. That reason raises nothing of its own; what the
 * server raises is `edge_gateway` for the service (named `robot` by
 * `edge/bootstrap.sh`, `robot_bridge` on older boxes), and the bell shows
 * it. Without this the card read "Nexsoss may not know. Please call."
 * beside an open alert saying the same box was down.
 *
 * Matched by the service's NAME: which services carry which robots is
 * not established (plan check L7). Only an open alert counts -- the rule
 * is in no `SERVER_RULE_DWELLS`, so nothing is promised before one opens.
 */
function evidenceKeys(ctx: Ctx, c: RobotCondition): AlertKey[] {
  if (c.row.key !== 'edge_offline' || !ctx.alertsOk) return c.alertKeys;
  const gateways = ctx.input.alerts.rows.flatMap((a): AlertKey[] => {
    const service = a.sourceId || a.source || '';
    const rule = alertRule(a);
    return rule === 'edge_box_offline' || (rule === 'edge_gateway' && /robot/i.test(service))
      ? [{ rule: rule, sourceKind: a.sourceKind ?? 'gateway', sourceId: service }]
      : [];
  });
  return [...c.alertKeys, ...gateways];
}

/** A copy row's headline for this reader. An unknown category's text is
 *  the robot's own sentence -- often a code ("WHEEL_SLIP: left") -- and a
 *  site reader never sees it: the card says it needs a check, as the
 *  toast does. Ops keep the raw text, quoted, and in Details. */
function headlineFor(ctx: Ctx, row: RobotCopyRow, words: Parameters<RobotCopyRow['headline']>[0]): string {
  return ctx.audience === 'site' && row.key === 'unmatched' ? NEEDS_A_CHECK : row.headline(words);
}

/**
 * A robot card's reports, most urgent first. The first heads the card:
 * its group, steps and evidence line are that report's.
 *
 * A site report about safety comes first (a robot in a lift doorway, its
 * red button pressed): someone has to go now. Then a ranked-first report,
 * whoever acts: a guest lift held for a robot that is not in it, which only
 * Nexsoss can free. Under "who acts first" a failed dock or a blocked way
 * (the site's since 2026-10-20) headed that card, the car went to "Also"
 * and nobody was asked to call. Then who acts, then band.
 *
 * While the site box is silent (`edge_offline`), what it last said about
 * the robot (`not_reporting`, held by `robotCannotRead`) ranks as the
 * box's own report, just below it. It is only the box's last word, so the
 * card stays the box's: with Nexsoss, naming the box's open alert
 * (`evidenceKeys`), not sending staff to check a robot's power while the
 * box is what is down.
 *
 * Among the server's alert-row reports (api-server 0.6.42), one it raised
 * to critical comes first, and a stall comes after any report it stands
 * level with: "stuck in one spot" says only that the robot is getting
 * nowhere, and a door it is waiting at, or a way it can't get through,
 * says why. Neither
 * tie-break can reorder a card without one of those reports on it.
 */
function byUrgency(conditions: readonly RobotCondition[]): RobotCondition[] {
  const boxSilent = conditions.some((c) => c.row.key === 'edge_offline');
  const lastWord = (c: RobotCondition): boolean => boxSilent && c.row.key === 'not_reporting';
  const siteSafety = (c: RobotCondition): number => (c.row.whoActs === 'you' && c.row.band === 'safety' ? 0 : 1);
  const who = (c: RobotCondition): number => WHO_RANK[lastWord(c) ? 'nexsoss' : c.row.whoActs];
  return [...conditions].sort(
    (a, b) =>
      siteSafety(a) - siteSafety(b) ||
      Number(Boolean(b.row.rankFirst)) - Number(Boolean(a.row.rankFirst)) ||
      who(a) - who(b) ||
      BAND_RANK[a.row.band] - BAND_RANK[b.row.band] ||
      Number(lastWord(a)) - Number(lastWord(b)) ||
      Number(Boolean(b.critical)) - Number(Boolean(a.critical)) ||
      Number(a.row.key === 'robot_stalled') - Number(b.row.key === 'robot_stalled'),
  );
}

/**
 * `not_reporting` under an open `edge_box_offline` is the box's report, not
 * the robot's. A box that dies takes the bridge's last will with it (the
 * on-box broker holds it), so the fleet adapter never says `edge_offline`:
 * it says the bridge is fine and the ROBOT stopped, and that card sends
 * hotel staff to look for a robot's power switch because of a box in a
 * cupboard. The key stays, since a key is a reading's identity
 * (`robotConditionsFor`); only the words and who acts follow the box.
 */
function withBoxSilent(ctx: Ctx, conditions: RobotCondition[]): RobotCondition[] {
  if (!conditions.some((c) => c.row.key === 'not_reporting')) return conditions;
  const boxOffline = ctx.alertsOk && ctx.input.alerts.rows.some((a) => alertRule(a) === 'edge_box_offline');
  if (!boxOffline) return conditions;
  return conditions.map((c) => (c.row.key === 'not_reporting' ? { ...c, row: robotCopyRow('edge_offline') } : c));
}

function robotItem(
  ctx: Ctx,
  rawConditions: RobotCondition[],
  state: ItemState,
  alertBacked: AttentionAlert | null,
  held: HeldReports | null = null,
): AttentionItem {
  const conditions = withBoxSilent(ctx, rawConditions);
  const sorted = byUrgency(conditions);
  const head = sorted[0]!;
  // A held head is worded from the robot as it last read (A7): its card
  // as it was, read as of when it stopped being readable.
  const heldSinceMs = held?.unreadSinceMs.get(head.key);
  // Any report on the card held up (the head, or one under "Also"): the
  // card says it can't read the robot now (N3).
  const anyHeld = held !== null && conditions.some((c) => held.unreadSinceMs.has(c.key));
  const robot = head.robot;
  const level = robotFloor(ctx, robot, conditions, robot.level);
  const words = wordsOf(head, robot.name, level);
  const status = statusFields(ctx, state);
  // A card that looks fixed or has cleared says only what it was and where
  // it stands: no evidence, no start stamp, no steps and no e-stop card --
  // there is nothing to do, and nothing an alert could be claimed for.
  const done = state.status !== 'active';
  // A row the site fixes first (a lost robot, a blocked way or charger)
  // never asks a site reader to call up front -- its last step says when,
  // or it is wholly the site's -- so an overdue alert's line keeps saying
  // nobody has picked it up but drops the ask, and a line that is only an
  // ask ("Nexsoss may not know. Please call.") is not shown to them.
  // "Picked up by Nexsoss" and the like still are, and ops read every line
  // as it is. The alert is still accounted for either way.
  const siteFirst = head.row.siteFixesFirst === true && ctx.audience !== 'ops';
  const found = done
    ? null
    : evidence(
        ctx,
        evidenceKeys(ctx, head),
        head.fingerprint ? [head.fingerprint] : undefined,
        status.seenSinceMs,
        siteFirst,
      );
  const ev = found && found.pleaseCall && siteFirst ? null : found;
  const opened = done
    ? undefined
    : alertBacked
      ? alertBacked.openedAtMs
      : ctx.input.alerts.rows.find((a) => a.id === found?.alertId)?.openedAtMs;
  if (!done) {
    for (const c of conditions) {
      claimAlerts(ctx, evidenceKeys(ctx, c).filter((k) => k.rule !== 'robot_issue'));
    }
  }
  // Held, the job is the one it last read, which nobody has read since:
  // its timing stops there, so the card neither escalates ("Cancel hasn't
  // taken effect") nor changes, and the floor is where it was last seen.
  const estop =
    !done && head.row.key === 'emergency_stop'
      ? estopCard({
          robot: robot.name,
          level,
          job: robot.job,
          heldLift: estopCar(robot, conditions),
          handedBack: estopHandedBack(conditions),
          nowMs: heldSinceMs ?? ctx.input.nowMs,
          lastSeen: heldSinceMs !== undefined,
          audience: ctx.audience,
          fireActive: ctx.input.fireActive,
        })
      : null;
  return {
    key: `robot:${robotId(robot)}`,
    subject: { kind: 'robot', robots: [{ fleet: robot.fleet, name: robot.name }] },
    group: GROUP_OF[head.row.whoActs],
    band: head.row.band,
    rankFirst: Boolean(head.row.rankFirst),
    critical: head.critical === true,
    title: robot.name,
    headline: headlineFor(ctx, head.row, words),
    whoActsLine: null,
    also: sorted.slice(1).map((c) => headlineFor(ctx, c.row, wordsOf(c, robot.name, level))),
    lines: anyHeld ? [robotCantReadNowText(robot.name)] : [],
    ...stepsFor(ctx, estop || done ? [] : head.row.steps(words)),
    estop,
    // Where it is NOW, as the live record trusts it: never the floor a
    // held report last read (W3 withdraws a lost robot's floor), and never
    // the floor it boarded a car on (`robotFloor`).
    level: heldSinceMs !== undefined ? robotFloor(ctx, robot, conditions, held!.levelNow) : level,
    sinceMs: typeof opened === 'number' ? opened : null,
    sinceSource: typeof opened === 'number' ? 'alert' : null,
    evidence: ev,
    ...status,
    folded: [],
    details: ctx.audience === 'ops' ? sorted.map((c) => c.raw) : [],
    copyKey: head.arg ? `${head.row.key}:${head.arg}` : head.row.key,
    wallNowMs: ctx.input.nowMs,
  };
}

/** A robot condition stood in for by an open alert the page has no live
 *  report for (the alert came first, or the robot list lags). */
function conditionFromAlert(
  alert: AttentionAlert,
  robots: readonly AttentionRobotInput[],
): RobotCondition | null {
  const subject = alertSubject(alert);
  if (!subject) return null;
  let name: string | null = null;
  let row: RobotCopyRow;
  let arg: string | null = null;
  let raw: string;
  let fingerprint: string | null = null;
  switch (subject.kind) {
    case 'robot_issue': {
      const m = robotCopyForCategory(subject.category);
      name = subject.robot;
      row = m.row;
      arg = m.arg;
      raw = subject.category;
      fingerprint = `robot_issue:${subject.robot}:${subject.category}`;
      break;
    }
    case 'robot_error':
      name = subject.robot;
      row = robotCopyRow('robot_error');
      raw = 'robot_error';
      break;
    case 'robot_unreachable':
      name = subject.robot;
      row = robotCopyRow('state_channel_stale');
      raw = 'robot_unreachable';
      break;
    case 'task_attention':
      name = subject.robot;
      row = robotCopyRow(subject.liftRule ? 'stuck_task_lift_rule' : 'stuck_task');
      raw = `task ${subject.task}`;
      break;
    case 'task_long_running':
      name = subject.robot;
      row = robotCopyRow('long_task');
      arg = subject.minutes === null ? null : String(subject.minutes);
      raw = `task ${subject.task}`;
      break;
    default:
      return null;
  }
  if (!name) return null;
  const known = robots.find((r) => r.name === name);
  const robot: AttentionRobotInput = known ?? {
    fleet: '',
    name,
    status: 'unknown',
    issues: [],
    level: null,
    simulated: false,
    job: { state: 'none' },
    heldLift: null,
    presence: null,
  };
  return {
    key: `alert:${alert.id}`,
    robot,
    row,
    arg,
    raw,
    fingerprint,
    alertKeys: [
      {
        rule: alert.rule ?? subject.kind,
        sourceKind:
          alert.sourceKind ??
          (subject.kind === 'task_attention' || subject.kind === 'task_long_running'
            ? 'task'
            : 'robot'),
        sourceId: alert.sourceId || alert.source || name,
      },
    ],
  };
}

/** One open alert of an alert-row rule (`ALERT_ROW_RULES`), and who it
 *  is about now. */
interface AlertRowReport {
  alert: AttentionAlert;
  rule: AlertRowRule;
  /** The door the report names: a door rule's own, or the door a stalled
   *  robot is stuck at (`stallDoor`); null for a stall elsewhere. */
  door: string | null;
  /** That door's floor on the building map, when known. */
  doorLevel: string | null;
  /** A stall's door has opened for it (`StallDoor.opened`). */
  doorOpened: boolean;
  /** A stall at no door named, while whether it waits at one can't be
   *  told (`StallDoorReading`). */
  doorUnknown: boolean;
  /** `robot_stalled`'s robot, or the robot a door rule is about
   *  (`doorAlertRobot`); null for a door no robot can be named for. */
  robot: { fleet: string | null; name: string } | null;
}

/** The door a stalled robot is stuck at (`stallDoor`). */
export interface StallDoor {
  door: string;
  /**
   * The door has opened since this page first found the robot stuck at it:
   * it read open -- whoever asked, a porter's Open on the console too -- or
   * its last request is the robot's own CLOSE. RMF asks for one only once
   * the door has opened and the robot's leg through it has ended, which in
   * the re-ask loop of 2026-09-07 was the vendor reporting "reached" short
   * of the doorway, about 15 s before RMF asked for the door again. What it
   * can't get through is then the doorway (`RobotCopyContext.doorOpened`).
   * Once true it stays true for the alert: the door shuts behind every
   * close and opens again on every re-ask.
   */
  opened: boolean;
}

/**
 * Where a stalled robot is stuck, as this page reads it (`stallDoor`): at
 * a door; at none (null); or UNPLACED -- the door's last request is an
 * open of the robot's own too old to name the door alone
 * (`STALL_OPEN_ALONE_MS`), and where the robot or the door stands is not
 * known (`standsAtDoor`), so whether it is at that door can't be told.
 * An unplaced stall's card names no door and offers no cancel
 * (`RobotCopyContext.doorUnknown`), and nothing is kept for it: it is
 * read again next time (`rememberStallDoors`).
 */
export type StallDoorReading = StallDoor | 'unplaced' | null;

/**
 * How long before a `robot_stalled` row opened the robot's own OPEN may
 * have been made and name its door wherever on the floor the robot is:
 * the engine's minute before a stall is raised, plus its own slack
 * (`alerts_engine._ROBOT_STALL_DOOR_SLACK_MS`, 5 min, measured from when
 * the stall began), the bound spa 0.5.53 named every door by. A stall
 * begins at least a minute before its row opens, so the engine names
 * every such door too.
 */
const STALL_OPEN_ALONE_MS = 6 * 60_000;

/**
 * How near the door a stalled robot must stand for an older open of its
 * own to name it (`standsAtDoor`), measured to the map's first end of the
 * door: RMF waits for a door at the lane end before it, and a squeeze
 * swings up to 2 m about it. At sg-nomad-01 the ends of L4_SLL_Door's
 * lane are 1.3 m and 2.0 m from it, and all else within 5 m is the
 * L04-SLL lobby and the L04-Office waypoint just behind the door (map
 * 0.17.0).
 */
const STALL_DOOR_NEAR_M = 5;

/**
 * How long before a `robot_stalled` row opened the robot's own CLOSE may
 * have been made and still put it at that door, as the engine reads it
 * (`alerts_engine._DOOR_PASSAGE_RETRY_S`, 30 s): RMF releasing a doorway
 * the robot never cleared, about to ask again -- in the re-ask loop of
 * 2026-09-07, about 15 s after each close. An older close is a pass that
 * got through (every pass ends in one): a robot that drives through a
 * door and stops a few metres on is raised 50-56 s after its close, and
 * a minute here named the door it had got through (review of 0.5.54). A
 * close made since the row opened came while it was reported stuck.
 */
const STALL_CLOSE_BEFORE_ROW_MS = 30_000;

/** A robot as `stallDoor` places it: its floor and point. */
type StallRobot = Pick<AttentionRobotInput, 'name' | 'level' | 'at'>;

/** The door has opened for this robot (`StallDoor.opened`): it reads
 *  open, or its last request is the robot's own close. */
function doorOpenedFor(d: AttentionDoorInput, robot: string): boolean {
  const r = d.robotRequest;
  return d.open || (r !== null && r.robot === robot && r.requested === 'close');
}

/** The door is on another floor than the robot, both floors known. */
function onOtherFloor(level: string | null, d: AttentionDoorInput | undefined): boolean {
  return level !== null && d !== undefined && d.level !== null && d.level !== level;
}

/** The robot stands at the door: on its floor, within
 *  `STALL_DOOR_NEAR_M` of it. Null while either's floor or point is not
 *  known. */
function standsAtDoor(robot: StallRobot | undefined, d: AttentionDoorInput): boolean | null {
  const at = robot?.at;
  if (!at || !robot.level || d.at === null || d.level === null) return null;
  return robot.level === d.level && Math.hypot(at.x - d.at.x, at.y - d.at.y) <= STALL_DOOR_NEAR_M;
}

/**
 * The door a stalled robot is stuck at by the doors' live state now: the
 * first door by name (as `doors` is sorted) whose last request is this
 * robot's own, and puts it there:
 *
 *   - its OPEN, made no more than `STALL_OPEN_ALONE_MS` before the row
 *     opened, as the engine names a door in its own message;
 *   - an older OPEN, while the robot stands at the door (`standsAtDoor`);
 *   - its CLOSE, since `STALL_CLOSE_BEFORE_ROW_MS` before the row opened.
 *
 * AN OLD OPEN IS NOT A STALE ONE. RMF re-sends an open every second until
 * the door opens, however long that takes, and the worker keeps its FIRST
 * stamp: a robot shut out for 9 min holds a 9-min-old open, and so does
 * a stall raised again after a hand resolve or a door alert's quiet. By
 * its age alone (spa 0.5.53) such a stall lost its door and told staff to
 * cancel its job at a shut door (review of 0.5.53).
 *
 * NOR IS IT ALWAYS THE ROBOT'S DOOR. An open RMF never followed with a
 * close (a job cancelled at the door, an adapter restart mid-pass) stays
 * the door's last request until anyone asks it again -- and the robot's
 * own next ask of that door keeps the old stamp. With no age bound (spa
 * 0.5.54 as first built) a stall anywhere on the door's floor was sent to
 * that door: at sg-nomad-01 all of L4 but its lobby is behind
 * L4_SLL_Door (review of 0.5.54). So an old open names the door only
 * where the robot stands at it; while that can't be told, the reading is
 * `unplaced`.
 *
 * A DOOR ON ANOTHER FLOOR is never named, when both floors are known.
 */
function stallDoorNow(
  doors: readonly AttentionDoorInput[] | undefined,
  robot: string,
  who: StallRobot | undefined,
  openedAtMs: number | undefined,
): StallDoorReading {
  let unplaced = false;
  for (const d of doors ?? []) {
    const r = d.robotRequest;
    if (r === null || r.robot !== robot) continue;
    if (onOtherFloor(who?.level ?? null, d)) continue;
    let here: boolean | null;
    if (r.requested === 'close') {
      here = typeof openedAtMs === 'number' && r.atMs > openedAtMs - STALL_CLOSE_BEFORE_ROW_MS;
    } else if (typeof openedAtMs === 'number' && r.atMs >= openedAtMs - STALL_OPEN_ALONE_MS) {
      here = true;
    } else {
      here = standsAtDoor(who, d);
    }
    if (here === null) unplaced = true;
    else if (here) return { door: d.name, opened: doorOpenedFor(d, robot) };
  }
  return unplaced ? 'unplaced' : null;
}

/**
 * Where a `robot_stalled` alert's robot is stuck (`StallDoorReading`): the
 * door this page first found it at (`remembered`, by alert id:
 * `rememberStallDoors`, which the hook keeps), else the doors' live
 * state's (`stallDoorNow`); null for a stall at no door, and for any
 * other rule.
 *
 * THE FIRST DOOR HOLDS while the alert is open, as `doorAlertRobot`'s
 * first robot does (review of 0.5.53). Read afresh on every render, the
 * card followed the door: in the re-ask loop it flipped every few seconds
 * between "waiting for it to open" and "stuck in one spot" with a cancel,
 * and once a porter opened the door by hand it said to cancel the job
 * while the robot drove through. Only `opened` moves, and only on: the
 * remembered value comes back as it was until the door opens for it.
 * Save one case: a door found while the robot's floor or the door's was
 * not yet known (page load: the building map's registry and presence come
 * after the doors) is dropped once both are known and differ, and read
 * again (review of 0.5.54).
 */
export function stallDoor(
  alert: AttentionAlert,
  robots: readonly StallRobot[],
  doors: readonly AttentionDoorInput[] | undefined,
  remembered: ReadonlyMap<string, StallDoor> | undefined,
): StallDoorReading {
  const subject = alertSubject(alert);
  if (subject?.kind !== 'robot_stalled') return null;
  const who = robots.find((r) => r.name === subject.robot);
  const kept = remembered?.get(alert.id);
  const d = kept && doors?.find((x) => x.name === kept.door);
  if (kept && !onOtherFloor(who?.level ?? null, d)) {
    return !kept.opened && d && doorOpenedFor(d, subject.robot)
      ? { door: kept.door, opened: true }
      : kept;
  }
  return stallDoorNow(doors, subject.robot, who, alert.openedAtMs);
}

/**
 * Every open alert of an alert-row rule. The robot a door rule is about is
 * the one the door's live state names (`doorAlertRobot`) -- the engine
 * names the same one in its message from the same record, but a message is
 * never read for it -- so a door whose state is not known, or whose last
 * request is not a robot's, names none unless this page named one for the
 * alert before. A stalled robot stuck at a door names that door
 * (`stallDoor`), as the engine's own message does.
 */
function alertRowReports(input: AttentionInput): AlertRowReport[] {
  const out: AlertRowReport[] = [];
  for (const alert of input.alerts.rows) {
    const subject = alertSubject(alert);
    if (subject?.kind === 'robot_stalled') {
      const read = stallDoor(alert, input.robots.rows, input.doors, input.stallDoors);
      const at = read === 'unplaced' ? null : read;
      out.push({
        alert,
        rule: subject.kind,
        door: at?.door ?? null,
        doorLevel: at ? (input.doors?.find((d) => d.name === at.door)?.level ?? null) : null,
        doorOpened: at?.opened === true,
        doorUnknown: at === null && (read === 'unplaced' || (input.doorsHealth ?? 'ok') !== 'ok'),
        robot: { fleet: null, name: subject.robot },
      });
    } else if (subject?.kind === 'door_open_failed' || subject?.kind === 'door_passage_stalled') {
      const door = input.doors?.find((d) => d.name === subject.door);
      const named = doorAlertRobot(alert.id, door, input.doorAlertRobots);
      out.push({
        alert,
        rule: subject.kind,
        door: subject.door,
        doorLevel: door?.level ?? null,
        doorOpened: false,
        doorUnknown: false,
        robot: named ? { fleet: named.fleet, name: named.robot } : null,
      });
    }
  }
  return out;
}

/** The alert an alert-row report rests on, as its own row names it. */
function alertRowKey({ alert, rule }: AlertRowReport): AlertKey {
  return {
    rule,
    sourceKind: alert.sourceKind ?? (rule === 'robot_stalled' ? 'robot' : 'door'),
    sourceId: alert.sourceId || alert.source || '',
  };
}

/** An alert-row report as one of `robot`'s reports: its card's head when
 *  it is the most urgent (`byUrgency`), else a line under "Also". */
function alertRowCondition(report: AlertRowReport, robot: AttentionRobotInput): RobotCondition {
  const { alert, rule, door } = report;
  return {
    key: `alert:${alert.id}`,
    robot,
    row: robotCopyRow(rule),
    arg: door,
    floor: report.doorLevel,
    raw: door !== null ? `${rule}: ${door}` : rule,
    fingerprint: null,
    alertKeys: [alertRowKey(report)],
    critical: alert.severity === 'critical',
    doorOpened: report.doorOpened,
    doorUnknown: report.doorUnknown,
  };
}

const isCritical = (r: AlertRowReport): boolean => r.alert.severity === 'critical';

/**
 * A door's own card, for its alerts no robot can be named for
 * (`doorAlertRobot`): the door's state is not known, or its last request
 * is not a robot's (a console Open that failed), and this page named no
 * robot for the alert before. Who acts, band and site-first are the robot
 * rows' of the same rule; the words are the door's (`DOOR_CARD_COPY`). One
 * card per door: a report the server raised to critical heads it.
 */
function doorItem(ctx: Ctx, door: string, reports: readonly AlertRowReport[]): AttentionItem {
  const sorted = [...reports].sort((a, b) => Number(isCritical(b)) - Number(isCritical(a)));
  const head = sorted[0]!;
  const rule = head.rule as DoorCardKey;
  const row = robotCopyRow(rule);
  claimAlerts(ctx, reports.map(alertRowKey));
  // As a robot card's (`robotItem`): a site reader is never asked to call
  // up front, so an overdue line drops its ask; ops read it as it is. The
  // card rests on its own open row, so the line always names that row:
  // never the ask-only line a card with no alert would carry.
  const siteFirst = row.siteFixesFirst === true && ctx.audience !== 'ops';
  const opened = head.alert.openedAtMs;
  return siteItem(`door:${door}`, door, row.whoActs, ctx.input.nowMs, {
    subject: { kind: 'door', door },
    band: row.band,
    critical: isCritical(head),
    headline: DOOR_CARD_COPY[rule].headline,
    also: sorted.slice(1).map((r) => DOOR_CARD_COPY[r.rule as DoorCardKey].headline),
    ...stepsFor(ctx, [...DOOR_CARD_COPY[rule].steps]),
    level: head.doorLevel,
    sinceMs: typeof opened === 'number' ? opened : null,
    sinceSource: typeof opened === 'number' ? 'alert' : null,
    evidence: evidence(ctx, [alertRowKey(head)], undefined, null, siteFirst),
    details: ctx.audience === 'ops' ? sorted.map((r) => `${r.rule}: ${door}`) : [],
    copyKey: `door:${rule}`,
  });
}

/** `atMs` is when its timing is read: now, or for a car that can't be
 *  read, when it stopped being readable (A7). */
function liftItem(
  ctx: Ctx,
  reading: LiftReadingInput,
  seenSinceMs: number,
  atMs: number = ctx.input.nowMs,
): AttentionItem | null {
  const att = liftAttention(reading, seenSinceMs, atMs);
  if (!att) return null;
  return {
    ...baseLift(ctx, reading),
    group: GROUP_OF[att.whoActs],
    band: att.band,
    whoActsLine: whoActsLineFor(ctx, att.whoActsLine, att.whoActs),
    ...stepsFor(ctx, att.steps),
    sinceMs: att.sinceMs,
    sinceSource: att.sinceMs !== null ? 'lift' : null,
    evidence: evidenceIfAsked(ctx, att.whoActs, att.alertKeys, seenSinceMs),
    ...statusFields(ctx, { status: 'active', seenSinceMs }),
  };
}

/** "Looks fixed" or "Cleared at" for a car: headed by what it WAS (W6's
 *  Robots line then, as a robot's card is headed by its report), over its
 *  CURRENT lines -- never the old ones, which would pass a past reading off
 *  as now -- and none when the car has no reading now. Nothing to do.
 *  While it looks fixed it stays in the group it was in; once cleared it
 *  needs nobody. */
function quietLiftItem(
  ctx: Ctx,
  was: Extract<ConditionAbout, { kind: 'lift' }>,
  now: LiftReadingInput | undefined,
  group: AttentionGroup,
  state: QuietState,
): AttentionItem {
  const current = now ? baseLift(ctx, now) : null;
  return {
    key: `lift:${was.lift}`,
    subject: { kind: 'lift', lift: was.lift },
    group,
    band: 'blocked',
    rankFirst: false,
    critical: false,
    title: was.lift,
    headline: was.robots,
    whoActsLine: null,
    also: [],
    lines: now ? notTheHeadline([now.guests, now.robots], was.robots) : [],
    steps: [],
    stepsHeading: null,
    estop: null,
    level: current ? current.level : null,
    sinceMs: null,
    sinceSource: null,
    evidence: null,
    ...statusFields(ctx, state),
    folded: [],
    details:
      ctx.audience === 'ops'
        ? [`${state.status === 'clearing' ? 'looks fixed' : 'cleared'}: ${was.reading}`, ...(current ? current.details : [])]
        : [],
    copyKey: `lift:${was.reading}`,
    wallNowMs: ctx.input.nowMs,
  };
}

/**
 * The card of a car that can't be read now (A7): what it had while it
 * read -- the same headline, said as last known ("Last known: Kept out
 * since 14:02", N5), who acts, steps and evidence, worded from
 * the reading that last showed it, with W5's own timing read as of when it
 * stopped being readable (a hold is not carded, nor a wait escalated,
 * while nobody can see it) -- over its CURRENT lines, which are W6's
 * words for why it can't be read ("No reading since 14:40", "Checking",
 * "Not known: this screen is reconnecting") -- and its CURRENT floor, as
 * a card that looks fixed has: never the floor it last read. Never "Looks
 * fixed": nothing says it is. Null when it had no card when it stopped
 * being readable.
 */
function heldLiftItem(
  ctx: Ctx,
  was: LiftAbout,
  view: { seenSinceMs: number; unreadSinceMs?: number },
  now: LiftReadingInput | undefined,
): AttentionItem | null {
  const item = liftItem(ctx, was.last, view.seenSinceMs, view.unreadSinceMs ?? ctx.input.nowMs);
  if (!item) return null;
  const why = now ? `${now.kind}${now.lostBecause ? ` (${now.lostBecause})` : ''}` : 'no reading';
  return {
    ...item,
    headline: lastKnownText(item.headline),
    lines: now ? [now.guests, now.robots] : [],
    level: now ? now.level : null,
    details: ctx.audience === 'ops' ? [...item.details, `can't read now: ${why}`] : [],
  };
}

/** A car's lines without the one its card is already headed by: the
 *  Robots line heads the card, and said again under it the card read
 *  "phantas_01 waiting for it since 14:02" twice (copy pass 2026-10-05). */
function notTheHeadline(lines: readonly string[], headline: string): string[] {
  return lines.filter((line) => line !== headline);
}

function baseLift(ctx: Ctx, reading: LiftReadingInput) {
  const refusal = reading.session?.refusal?.code;
  return {
    key: `lift:${reading.name}`,
    subject: { kind: 'lift', lift: reading.name } as AttentionSubject,
    rankFirst: false,
    critical: false,
    title: reading.name,
    headline: reading.robots,
    also: [] as string[],
    lines: notTheHeadline([reading.guests, reading.robots], reading.robots),
    estop: null,
    level: reading.level,
    folded: [] as Array<{ item: AttentionItem; join: string }>,
    details:
      ctx.audience === 'ops'
        ? [`reading: ${reading.kind}`, ...(refusal ? [`refusal: ${refusal}`] : [])]
        : [],
    copyKey: `lift:${reading.kind}`,
    wallNowMs: ctx.input.nowMs,
  };
}

function siteItem(
  key: string,
  text: string,
  whoActs: WhoActs,
  wallNowMs: number,
  extra: Partial<AttentionItem> = {},
): AttentionItem {
  return {
    key,
    subject: { kind: 'site', row: text },
    group: GROUP_OF[whoActs],
    band: 'blocked',
    rankFirst: false,
    critical: false,
    title: text,
    headline: text,
    whoActsLine: null,
    also: [],
    lines: [],
    steps: [],
    stepsHeading: null,
    estop: null,
    level: null,
    sinceMs: null,
    sinceSource: null,
    seenSinceMs: null,
    evidence: null,
    status: 'active',
    statusLine: null,
    lookedFixedAtMs: null,
    clearedAtMs: null,
    folded: [],
    details: [],
    copyKey: key,
    wallNowMs,
    ...extra,
  };
}

/**
 * A robot's last status, in words that follow "last reported". The status
 * is an internal token (`pending-localize`, `offline`...), never hotel
 * copy; ops get the token itself under Details.
 */
const FIRE_STATUS_WORDS: Readonly<Record<string, string>> = {
  idle: 'idle',
  charging: 'charging',
  active: 'working',
  error: 'an error',
  'pending-localize': 'that it had lost its place',
};

/** Fire: per robot, only what the data shows (Decision 17). */
function fireLines(ctx: Ctx, robots: readonly AttentionRobotInput[]): string[] {
  return robots.map((r) => {
    const level = levelWords(r.level);
    const where = level ? ` on ${level}` : '';
    const when =
      typeof r.lastReportAtMs === 'number'
        ? ` at ${dayClockText(r.lastReportAtMs, ctx.input.clock, ctx.input.nowMs)}`
        : '';
    const held = r.issues.includes('confirm which floor this robot is on');
    const words = FIRE_STATUS_WORDS[r.status];
    // `offline` is the platform's word for "not heard from": not something
    // the robot reported. An unknown status is not quoted at all.
    const last =
      r.status === 'offline'
        ? `last reported${where}${when}; not reporting now`
        : `last reported${words ? ` ${words}` : ''}${where}${when}`;
    return held ? `${r.name} is held after the fire alarm; ${last}.` : `${r.name} ${last}.`;
  });
}

function stampOrder(a: AttentionItem, b: AttentionItem): number {
  // Stamped first, oldest first; then unstamped by "seen here since".
  if (a.sinceMs !== null && b.sinceMs !== null) return a.sinceMs - b.sinceMs;
  if (a.sinceMs !== null) return -1;
  if (b.sinceMs !== null) return 1;
  const sa = a.seenSinceMs ?? Number.POSITIVE_INFINITY;
  const sb = b.seenSinceMs ?? Number.POSITIVE_INFINITY;
  return sa === sb ? 0 : sa < sb ? -1 : 1;
}

/** Up first, then looking fixed, then cleared: only `active` asks anything
 *  of anyone. */
const STATUS_RANK: Readonly<Record<AttentionItem['status'], number>> = { active: 0, clearing: 1, cleared: 2 };

function order(items: AttentionItem[], group: AttentionGroup): AttentionItem[] {
  return [...items].sort(
    (a, b) =>
      STATUS_RANK[a.status] - STATUS_RANK[b.status] ||
      Number(b.rankFirst) - Number(a.rankFirst) ||
      (group === 'needs_you' ? BAND_RANK[a.band] - BAND_RANK[b.band] : 0) ||
      // Only an alert-row card is ever critical (`AttentionItem.critical`).
      Number(b.critical) - Number(a.critical) ||
      (group === 'nexsoss'
        ? Number(Boolean(b.evidence?.pleaseCall)) - Number(Boolean(a.evidence?.pleaseCall))
        : 0) ||
      stampOrder(a, b) ||
      a.key.localeCompare(b.key),
  );
}

/**
 * Whether a robot's card may share one card with other robots at all. A
 * merged card shows ONE set of steps and ONE evidence line, so it may only
 * hold robots for which both are true. Never merged:
 *   - a safety or ranked-first report: the steps are about THIS robot (the
 *     e-stop card, which is safety, depends on its job and on whether it is
 *     in a car -- Decision 4);
 *   - a robot whose floor is not known: "the same floor" is not known;
 *   - a row whose card is its robot's alone (`RobotCopyRow.ownCard`): its
 *     steps send the reader to THIS robot's panel, and a card about two
 *     robots offers no panel (`AttentionCard`).
 */
function coalescible(
  item: AttentionItem,
): item is AttentionItem & { subject: Extract<AttentionSubject, { kind: 'robot' }> } {
  return (
    item.subject.kind === 'robot' &&
    item.status === 'active' &&
    item.also.length === 0 &&
    item.band !== 'safety' &&
    !item.rankFirst &&
    item.level !== null &&
    // A robot card's copy key is its head row's key, then `:<arg>`.
    !OWN_CARD_ROWS.has(item.copyKey.split(':', 1)[0]!)
  );
}

/** The evidence line a merged card would show: every robot on it must
 *  carry the same one, or one robot's "Picked up" would hide another's
 *  "Please call". The text alone decides: every tier words itself
 *  differently, and a please-call is part of the text. */
function evidenceCause(ev: Evidence | null): string {
  return ev ? ev.text : '';
}

/** The same report on one floor, for several robots, becomes one card --
 *  where one card can be true of every robot on it. */
function coalesceSameCause(items: AttentionItem[]): AttentionItem[] {
  const out: AttentionItem[] = [];
  const byCause = new Map<string, AttentionItem>();
  for (const item of items) {
    if (!coalescible(item)) {
      out.push(item);
      continue;
    }
    const cause = `${item.copyKey}|${item.level}|${item.group}|${evidenceCause(item.evidence)}`;
    const first = byCause.get(cause);
    if (!first || first.subject.kind !== 'robot') {
      byCause.set(cause, item);
      out.push(item);
      continue;
    }
    first.subject.robots.push(...item.subject.robots);
    first.title = joinNames(first.subject.robots.map((r) => r.name));
    first.details.push(...item.details);
    // Keep the oldest stamps: the card has been true since the first.
    if (item.sinceMs !== null && (first.sinceMs === null || item.sinceMs < first.sinceMs)) {
      first.sinceMs = item.sinceMs;
      first.sinceSource = item.sinceSource;
    }
    if (item.seenSinceMs !== null && (first.seenSinceMs === null || item.seenSinceMs < first.seenSinceMs)) {
      first.seenSinceMs = item.seenSinceMs;
    }
  }
  return out;
}

export function selectAttention(input: AttentionInput): AttentionModel {
  const audience = audienceFor(input.roles);
  const alertsOk = input.alerts.health === 'ok';
  const rowsN = input.alerts.rows.length;
  const truncated =
    rowsN >= input.alerts.pageLimit ||
    (input.alerts.summaryOpen !== null && input.alerts.summaryOpen > rowsN);
  const ctx: Ctx = { input, audience, alertsOk, truncated, mapped: new Set() };

  // -- what the live reading shows ------------------------------------------
  const shown = shownConditions(input.robots.rows, input.roles);
  const live: LiveKeys = {
    robots: new Set(input.robots.rows.flatMap((r) => robotConditionsFor(r).map((c) => c.key))),
    lifts: new Set(liftConditionsShown(input.lifts.readings, input.lifts.siteRow).keys()),
  };
  const unread: Unread = {
    robots: robotCannotRead(input.robots, input.feed),
    lifts: liftCannotRead(input.lifts, input.feed),
  };
  const debounce = againstReading(input, live, unread);

  // -- robots ---------------------------------------------------------------
  // A report is on screen when the debounce has raised it, OR an open alert
  // backs it: the server already decided, so the page does not dwell again.
  const shownNames = new Set(shown.map((s) => s.robot.name));
  const robots = shown.map((s) => s.robot);
  const robotItems: AttentionItem[] = [];
  // A raised report while the robot can't be read (A7) is held up on the
  // robot's card, as it last read: its silence is not evidence of a fix.
  // WHETHER OR NOT THE READING STILL LISTS IT (A17b): a dark robot whose
  // registry still lists its stop is no more readable than one whose
  // registry dropped it, and its card -- the e-stop's clock included --
  // freezes at the same stamp (A11) rather than running on and turning
  // into "Cancel hasn't taken effect". Only one this viewer would have
  // been shown. The stamp is the debounce's: the hook folds a robot going
  // dark on the clock as a glance, so it is the flip's, not the next
  // poll's (A17b). A debounce that has not seen it go dark holds from now.
  const heldRobots = new Map<string, RobotCondition[]>();
  const heldSince = new Map<string, number>();
  for (const [key, view] of debounce) {
    if (view.status !== 'active' || view.about?.kind !== 'robot') continue;
    if (!unread.robots(key) || !robotVisible(input.roles, audience, view.about)) continue;
    const id = robotId(view.about.robot);
    heldRobots.set(id, [...(heldRobots.get(id) ?? []), heldRobotCondition(key, view.about)]);
    heldSince.set(key, view.unreadSinceMs ?? input.nowMs);
  }
  // The server's alert-row rules (api-server 0.6.42): each open row is one
  // more report of the robot it is about, on that robot's one card, where
  // the most urgent of all its reports heads it (`byUrgency`). A door's row
  // is about the robot `doorAlertRobot` names; one no robot can be named
  // for is the door's own card (`doorItem`, below). A robot this viewer is
  // not shown (a simulated one) has its rows accounted for on purpose, as
  // its own alerts are, and no card.
  const rowConditions = new Map<string, RobotCondition[]>();
  const doorReports = new Map<string, AlertRowReport[]>();
  const reports = alertsOk ? alertRowReports(input) : [];
  // The fleet a robot no reading lists is carded under: its row's, else
  // the one another row about it names. A stall row names no fleet, and
  // while the robots had not read (page load) a robot stuck at a door got
  // two cards, one from each rule (review of 0.5.53).
  const fleetNamed = new Map<string, string>();
  for (const { robot: who } of reports) {
    if (who?.fleet && !fleetNamed.has(who.name)) fleetNamed.set(who.name, who.fleet);
  }
  for (const report of reports) {
    const who = report.robot;
    if (who === null) {
      if (report.door !== null) doorReports.set(report.door, [...(doorReports.get(report.door) ?? []), report]);
      continue;
    }
    const named = input.robots.rows.filter((r) => r.name === who.name);
    const listed = named.find((r) => r.fleet === who.fleet) ?? named[0];
    if (listed && !robots.includes(listed)) {
      ctx.mapped.add(report.alert.id);
      continue;
    }
    // Not listed, but held up as it last read (the robots did not read):
    // that is its card, so the row joins it, never a second card for the
    // same robot under a fleet the row could not name.
    const heldNamed = listed
      ? []
      : [...heldRobots.values()].map((cs) => cs[0]!.robot).filter((r) => r.name === who.name);
    const known = listed ?? heldNamed.find((r) => r.fleet === who.fleet) ?? heldNamed[0];
    const robot: AttentionRobotInput = known ?? {
      fleet: who.fleet ?? fleetNamed.get(who.name) ?? '',
      name: who.name,
      status: 'unknown',
      issues: [],
      level: null,
      simulated: false,
      job: { state: 'none' },
      heldLift: null,
      presence: null,
    };
    const id = robotId(robot);
    rowConditions.set(id, [...(rowConditions.get(id) ?? []), alertRowCondition(report, robot)]);
  }
  /** This robot's alert-row reports (by `<fleet>/<robot>`), once. */
  const takeRowConditions = (robot: AttentionRobotInput): RobotCondition[] => {
    const id = robotId(robot);
    const found = rowConditions.get(id) ?? [];
    rowConditions.delete(id);
    return found;
  };

  /** `now`: the robot as the live reading lists it; null when it does
   *  not. */
  const robotCard = (conditions: RobotCondition[], now: AttentionRobotInput | null) => {
    const backed = conditions.map((c) => backingAlerts(ctx, c));
    for (const list of backed) for (const a of list) ctx.mapped.add(a.id);
    // A raised key the live reading shows is up, even while the debounce
    // still says "looks fixed": the reading is one render ahead of it, and
    // "Looks fixed" over a condition the reading shows would be false.
    const views = conditions.map((c) => raisedView(debounce.get(c.key)));
    const on = conditions.filter((_, i) => views[i] !== null || backed[i]!.length > 0);
    if (on.length === 0) return;
    const seen = views.map((v) => v?.seenSinceMs ?? null).filter((t): t is number => t !== null);
    const seenSinceMs = seen.length > 0 ? Math.min(...seen) : null;
    robotItems.push(
      robotItem(ctx, on, { status: 'active', seenSinceMs }, null, {
        unreadSinceMs: heldSince,
        levelNow: now?.level ?? null,
      }),
    );
  };
  for (const { robot, conditions } of shown) {
    const id = robotId(robot);
    const held = heldRobots.get(id) ?? [];
    // A held report stands in for the live one it is (A17b), never beside it.
    const heldKeys = new Set(held.map((c) => c.key));
    robotCard(
      [...conditions.filter((c) => !heldKeys.has(c.key)), ...held, ...takeRowConditions(robot)],
      robot,
    );
    heldRobots.delete(id);
  }
  // Held for a robot the reading does not list: the robots did not read.
  for (const conditions of heldRobots.values()) {
    robotCard([...conditions, ...takeRowConditions(conditions[0]!.robot)], null);
  }
  // An alert-row report about a robot the reading does not list.
  for (const conditions of rowConditions.values()) robotCard(conditions, null);

  // An open robot alert no live report accounts for: the robot stopped
  // reporting it, or is not in the list. It follows its alert.
  if (alertsOk) {
    for (const alert of input.alerts.rows) {
      if (ctx.mapped.has(alert.id)) continue;
      const subject = alertSubject(alert);
      if (!subject || !ROBOT_ALERT_RULES.has(subject.kind)) continue;
      const name = 'robot' in subject ? subject.robot : null;
      const hidden = input.robots.rows.find((r) => r.name === name && !shownNames.has(r.name));
      const cond = conditionFromAlert(alert, robots);
      if (hidden || !cond || (audience === 'site' && cond.row.opsOnly)) {
        // Accounted for on purpose (a simulated robot, an ops-only report),
        // so it is not counted among "other open alerts" either.
        if (hidden || cond) ctx.mapped.add(alert.id);
        continue;
      }
      ctx.mapped.add(alert.id);
      const at = robotItems.findIndex(
        (it) => it.subject.kind === 'robot' && it.subject.robots.some((r) => r.name === cond.robot.name),
      );
      if (at >= 0) {
        // One card per robot: a second report joins the card it has.
        robotItems[at]!.also.push(
          headlineFor(ctx, cond.row, {
            robot: cond.robot.name,
            arg: cond.arg,
            level: robotFloor(ctx, cond.robot, [cond], cond.robot.level),
          }),
        );
        continue;
      }
      robotItems.push(robotItem(ctx, [cond], { status: 'active', seenSinceMs: null }, alert));
    }
  }

  // "Looks fixed" (A4) and "Cleared at" (Decision 9) come from the
  // debounce view itself: a condition that has stopped showing is, by
  // definition, no longer in the live reading. Only for a robot with no
  // card up (one card per robot), never for a condition the reading shows
  // again, and never for one this viewer would not have been shown.
  const quietRobots = new Map<
    string,
    { conditions: RobotCondition[]; clearing: Array<{ seen: number; fixed: number }>; clearedAtMs: number }
  >();
  for (const [key, view] of debounce) {
    if (view.status === 'active' || view.about?.kind !== 'robot' || live.robots.has(key)) continue;
    if (!robotVisible(input.roles, audience, view.about)) continue;
    const { robot, copy, arg, raw } = view.about;
    const row = robotCopyRow(copy);
    const carded = robotItems.some(
      (it) => it.subject.kind === 'robot' && it.subject.robots.some((r) => r.name === robot.name),
    );
    if (carded) continue;
    const id = robotId(robot);
    const entry = quietRobots.get(id) ?? { conditions: [], clearing: [], clearedAtMs: Number.NEGATIVE_INFINITY };
    entry.conditions.push({ key, robot, row, arg, raw, fingerprint: null, alertKeys: [] });
    if (view.status === 'clearing') {
      entry.clearing.push({ seen: view.seenSinceMs, fixed: view.lookedFixedAtMs });
    } else {
      entry.clearedAtMs = Math.max(entry.clearedAtMs, view.clearedAtMs);
    }
    quietRobots.set(id, entry);
  }
  for (const { conditions, clearing, clearedAtMs } of quietRobots.values()) {
    // The card has cleared only once all of it has: while any report is
    // still in its dwell, the card looks fixed -- since the last of them
    // looked fixed -- and "Cleared at" is the latest clear.
    const state: ItemState =
      clearing.length > 0
        ? {
            status: 'clearing',
            seenSinceMs: Math.min(...clearing.map((c) => c.seen)),
            lookedFixedAtMs: Math.max(...clearing.map((c) => c.fixed)),
          }
        : { status: 'cleared', clearedAtMs };
    robotItems.push(robotItem(ctx, conditions, state, null));
  }

  // -- doors no robot can be named for ----------------------------------------
  const doorItems = [...doorReports].map(([door, reports]) => doorItem(ctx, door, reports));

  // -- lifts ----------------------------------------------------------------
  const liftItems: AttentionItem[] = [];
  for (const reading of input.lifts.readings) {
    const key = liftConditionKey(reading);
    // Up while the reading shows it, as for robots.
    const view = key ? raisedView(debounce.get(key)) : null;
    if (!view) continue;
    const item = liftItem(ctx, reading, view.seenSinceMs);
    if (item) liftItems.push(item);
  }
  // A car that can't be read now keeps the card it had (A7), until a card
  // of the reading's own is up for it -- "No reading" once raised -- or it
  // reads again. One per car: its latest raised condition. A held car card
  // can be one nobody is asked to act on (a hold carded as Info), which
  // the verdict's count passes over, so it is noted for the verdict: never
  // "All clear" over it. A held robot card always asks someone (no robot
  // copy row is Info), and a held site row names itself, as a row up does.
  let heldLiftCard = false;
  const heldLifts = new Map<string, { was: LiftAbout; view: { seenSinceMs: number; unreadSinceMs?: number } }>();
  for (const [key, view] of debounce) {
    if (view.status !== 'active' || view.about?.kind !== 'lift' || live.lifts.has(key)) continue;
    if (!unread.lifts(key)) continue;
    const was = view.about;
    if (liftItems.some((it) => it.subject.kind === 'lift' && it.subject.lift === was.lift)) continue;
    const prev = heldLifts.get(was.lift);
    if (!prev || view.seenSinceMs > prev.view.seenSinceMs) heldLifts.set(was.lift, { was, view });
  }
  for (const [lift, { was, view }] of heldLifts) {
    const item = heldLiftItem(ctx, was, view, input.lifts.readings.find((r) => r.name === lift));
    if (!item) continue;
    liftItems.push(item);
    heldLiftCard = true;
  }

  // Root cause first: a robot whose only report is a stuck job or a bare
  // error, waiting on a car that is itself raised, folds under the car.
  // The join is stated, never claimed as the cause.
  for (const lift of liftItems) {
    if (lift.status !== 'active' || lift.subject.kind !== 'lift') continue;
    const liftName = lift.subject.lift;
    const reading = input.lifts.readings.find((r) => r.name === liftName);
    for (const waiting of reading?.waitingRobots ?? []) {
      const idx = robotItems.findIndex(
        (it) =>
          it.status === 'active' &&
          it.subject.kind === 'robot' &&
          it.subject.robots.some((r) => r.name === waiting.name) &&
          FOLDS_UNDER_A_LIFT.has(it.copyKey) &&
          it.also.length === 0,
      );
      if (idx < 0) continue;
      const [folded] = robotItems.splice(idx, 1);
      lift.folded.push({ item: folded!, join: `${waiting.name} is waiting for ${liftName}.` });
    }
  }

  // -- site rows ------------------------------------------------------------
  const siteRows: AttentionItem[] = [];
  if (input.fireActive) {
    siteRows.push(
      siteItem('site:fire', FIRE_VERDICT, 'none', input.nowMs, {
        lines: fireLines(ctx, robots),
        details: audience === 'ops' ? robots.map((r) => `${r.name}: status ${r.status}`) : [],
      }),
    );
  }
  const absorbedLifts = new Set<string>();
  const row = input.lifts.siteRow;
  // W6's fire row gives way to the one above: fire is said once.
  const rowView = row && row.kind !== 'fire' ? raisedView(debounce.get(`site:lift:${row.kind}`)) : null;
  // A row W6 no longer shows because what decides it can't be read now
  // (A7: SL1 lost its reading, so "Robots can't change floors" became
  // "Can't tell ...") is still up -- in its own words, where it was, over
  // W6's row now as the line that says why -- until the row now has
  // raised and takes over. Never "Looks fixed": nothing says it is.
  const held = rowView === null && row?.kind !== 'fire' ? heldSiteView(debounce, live.lifts, unread.lifts) : null;
  const up =
    row && rowView
      ? { row, seenSinceMs: rowView.seenSinceMs, held: null }
      : held
        ? { row: held.was.row, seenSinceMs: held.seenSinceMs, held: held.was }
        : null;
  if (up) {
    const cars = siteRowCars(up.row, input.lifts.readings);
    for (const car of cars) absorbedLifts.add(car.name);
    const absorbed = liftItems.filter(
      (it) => it.subject.kind === 'lift' && absorbedLifts.has(it.subject.lift) && it.status === 'active',
    );
    // The car that decides who acts lends the row its who acts and its
    // who-acts line. A car with a card up speaks through its card (W5's
    // timing included). A robot car the row speaks for with no card of its
    // own -- in fire service, refusing but likely to clear by itself, read
    // while this screen reconnects -- speaks through W6's reading, so the
    // row never asks for a call the car itself says nobody needs. A guest
    // car never decides (Decision 3). Only a row about no robot car at all
    // falls back to the row's own default.
    const carded = new Set(absorbed.map((it) => (it.subject.kind === 'lift' ? it.subject.lift : '')));
    const voices = [
      ...absorbed.map((it) => ({ who: WHO_OF_GROUP[it.group], line: it.whoActsLine })),
      ...cars
        .filter((c) => c.robotUse !== 'none' && !carded.has(c.name))
        .map((c) => {
          const w = WHO_FROM_READING[c.whoActs];
          return { who: w, line: whoActsLineFor(ctx, c.whoActsLine, w) };
        }),
    ];
    // Stable: on a tie, a carded car's line.
    const decider = voices.sort((a, b) => WHO_RANK[a.who] - WHO_RANK[b.who])[0];
    // Lift control off while a planned maintenance window is open is part
    // of the stand-down that window is (copy deck v2, N4): the engine mutes
    // its rule then (A14), and asking hotel staff to call Nexsoss -- who
    // "may not know" -- about a stand-down Nexsoss planned is a claim the
    // data can't back. The row says so and asks nobody; an alert about it
    // that is already open is still placed here, not counted elsewhere.
    // Not over a car card of its own folded into the row (one held up from
    // before the disarm, A7): that card's ask is about the car, not the
    // stand-down, and it keeps it.
    const planned =
      up.row.kind === 'lift_control_off' && input.maintenanceUntilMs !== null && absorbed.length === 0;
    // A held row stays where it was, in the words and with the evidence
    // it had: its cars' readings now say only why it can't be read.
    const who: WhoActs = planned
      ? 'none'
      : up.held
        ? up.held.whoActs
        : decider
          ? decider.who
          : SITE_ROW_WHO_ACTS[up.row.kind];
    const line = planned
      ? LIFT_CONTROL_OFF_IN_MAINTENANCE
      : up.held
        ? whoActsLineFor(ctx, up.held.whoActsLine, who)
        : (decider?.line ?? null);
    // What the server would raise for these cars' readings, and for the
    // row itself ("Lift control off": the site's gate, A14): a row is never
    // promised an alert no rule will raise.
    const keys: AlertKey[] = [
      ...(up.held ? up.held.alertKeys : cars.flatMap(liftAlertKeys)),
      ...siteRowAlertKeys(up.row, input.siteId),
    ];
    const stamps = absorbed.map((it) => it.sinceMs).filter((s): s is number => s !== null);
    const opsNote = up.row.opsNote;
    if (planned) claimAlerts(ctx, keys);
    siteRows.push(
      siteItem(`site:lift:${up.row.kind}`, up.held ? lastKnownText(up.row.text) : up.row.text, who, input.nowMs, {
        whoActsLine: line,
        lines: up.held && row ? [row.text] : [],
        ...stepsFor(ctx, [...new Set(absorbed.flatMap((it) => it.steps))]),
        seenSinceMs: up.seenSinceMs,
        evidence: evidenceIfAsked(ctx, who, keys, up.seenSinceMs),
        folded: absorbed.flatMap((it) => it.folded),
        details:
          audience === 'ops'
            ? [
                ...(opsNote ? [opsNote] : []),
                ...absorbed.flatMap((it) => [it.title, ...it.details]),
                ...(up.held ? [`can't read now: ${row ? row.kind : 'no row'}`] : []),
              ]
            : [],
        sinceMs: stamps.length > 0 ? Math.min(...stamps) : null,
        sinceSource: stamps.length > 0 ? 'lift' : null,
      }),
    );
  }
  // A lift site row that looks fixed or has cleared, while no lift row of
  // W6's is up: none at all, or another kind still in its raise dwell (a
  // row that changed kind stays on screen until the new one is up). Not
  // beside the fire row: a row it masks may only be hidden. It says it
  // once: the cars it spoke for get no card of their own beside it, unless
  // a car is further from done than the row (a car looking fixed under a
  // row that has cleared).
  const quietAbsorbed = new Map<string, QuietState>();
  if (up === null && row?.kind !== 'fire') {
    const quiet = quietSiteView(debounce, live.lifts);
    if (quiet) {
      const was = quiet.was.row;
      for (const car of was.lifts) quietAbsorbed.set(car, quiet.state);
      // While it looks fixed it stays in the group it was in; once cleared
      // it needs nobody.
      siteRows.push(
        siteItem(`site:lift:${was.kind}`, was.text, quiet.state.status === 'clearing' ? quiet.was.whoActs : 'none', input.nowMs, {
          ...statusFields(ctx, quiet.state),
          details: audience === 'ops' && was.opsNote ? [was.opsNote] : [],
        }),
      );
    }
  }
  if (input.maintenanceUntilMs !== null) {
    // Its title can BE the verdict (the only row up), so its time names
    // the day as the verdict's own do: a window ending tomorrow morning
    // never reads as this morning.
    siteRows.push(
      siteItem('site:maintenance',
        `Planned maintenance until ${dayClockText(input.maintenanceUntilMs, input.clock, input.nowMs)}: robots take no new jobs`,
        'none',
        input.nowMs,
      ),
    );
  }
  const lifts = liftItems.filter(
    (it) => !(it.subject.kind === 'lift' && absorbedLifts.has(it.subject.lift)),
  );
  // "Looks fixed" and "Cleared at" for a car, from the debounce view as
  // for robots: only for a car with no card up and not spoken for by a
  // site row, never over the same condition showing again, and only for a
  // card that was ever up -- a hold that ended before its 5 minutes had no
  // card to look fixed. A car now showing ANOTHER condition still dwelling
  // keeps its card (A4): it vanished there before, under "All clear".
  // Looking fixed outranks a clear.
  const quietLifts = new Map<string, { was: LiftAbout; group: AttentionGroup; state: QuietState; at: number }>();
  for (const [key, view] of debounce) {
    if (view.status === 'active' || view.about?.kind !== 'lift' || live.lifts.has(key)) continue;
    const was = view.about;
    if (absorbedLifts.has(was.lift)) continue;
    if (liftItems.some((it) => it.subject.kind === 'lift' && it.subject.lift === was.lift)) continue;
    if (!liftWasCarded(was, view)) continue;
    // Seen for this long before the first reading that no longer showed it.
    const end = view.status === 'clearing' ? view.lookedFixedAtMs : view.clearedAtMs;
    const state: QuietState =
      view.status === 'clearing'
        ? { status: 'clearing', seenSinceMs: view.seenSinceMs, lookedFixedAtMs: view.lookedFixedAtMs }
        : { status: 'cleared', clearedAtMs: view.clearedAtMs };
    const group: AttentionGroup =
      view.status === 'clearing' ? GROUP_OF[liftWhoActs(was.reading, was.whoActs, end - view.seenSinceMs)] : 'info';
    const prev = quietLifts.get(was.lift);
    const rank = (s: QuietState) => QUIET_RANK[s.status];
    if (!prev || rank(state) > rank(prev.state) || (rank(state) === rank(prev.state) && end > prev.at)) {
      quietLifts.set(was.lift, { was, group, state, at: end });
    }
  }
  for (const [lift, { was, group, state }] of quietLifts) {
    const row = quietAbsorbed.get(lift);
    if (row && QUIET_RANK[row.status] >= QUIET_RANK[state.status]) continue;
    lifts.push(quietLiftItem(ctx, was, input.lifts.readings.find((r) => r.name === lift), group, state));
  }

  // -- groups ---------------------------------------------------------------
  const all = coalesceSameCause([...robotItems, ...doorItems, ...lifts]);
  const needsYou = order(all.filter((it) => it.group === 'needs_you'), 'needs_you');
  const nexsossAll = all.filter((it) => it.group === 'nexsoss');
  const collapsed = (it: AttentionItem) => Boolean(it.evidence?.collapses) && it.status === 'active';
  const pickedUp = order(nexsossAll.filter(collapsed), 'nexsoss');
  const nexsoss = order(nexsossAll.filter((it) => !collapsed(it)), 'nexsoss');
  const info = order(all.filter((it) => it.group === 'info'), 'info');

  // -- other open alerts ----------------------------------------------------
  // Every open row the list holds that no item accounts for, plus every
  // row past the end of a capped page. "All clear" never hides them. A
  // link to Alerts for ops who may open it: the support roles read ops
  // but are not let in (`ALERTS_ROLES`), so a link would only bounce them
  // back to the Overview. Everyone else reads the count.
  let otherOpenAlerts: AttentionModel['otherOpenAlerts'] = null;
  if (alertsOk && input.alerts.summaryOpen !== null) {
    const unplaced = input.alerts.rows.filter((a) => !ctx.mapped.has(a.id)).length;
    const count = unplaced + Math.max(0, input.alerts.summaryOpen - rowsN);
    if (count > 0) {
      otherOpenAlerts = {
        count,
        text: `${count} other open alert${count === 1 ? '' : 's'}`,
        linkToAlerts: audience === 'ops' && hasAnyRole(input.roles, ALERTS_ROLES),
      };
    }
  }

  return {
    verdict: verdictFor(
      input,
      { siteRows, needsYou, nexsoss, pickedUp, info, otherOpenAlerts },
      anyLooksFixed(input, audience, debounce, live),
      heldLiftCard ? ['lifts'] : [],
    ),
    siteRows,
    needsYou,
    nexsoss,
    pickedUp,
    info,
    otherOpenAlerts,
  };
}

function verdictFor(
  input: AttentionInput,
  m: Pick<AttentionModel, 'siteRows' | 'needsYou' | 'nexsoss' | 'pickedUp' | 'info' | 'otherOpenAlerts'>,
  looksFixed: boolean,
  /** Sources with a card held up while they can't be read (A7). */
  held: readonly Source[],
): AttentionModel['verdict'] {
  const cantCheck: string[] = [];
  if (input.robots.health === 'failed') cantCheck.push('robots');
  if (input.lifts.health === 'failed') cantCheck.push('lifts');
  if (input.alerts.health === 'failed' || (input.alerts.health === 'ok' && input.alerts.summaryOpen === null)) {
    cantCheck.push('alerts');
  }
  const loading = [input.robots.health, input.lifts.health, input.alerts.health].includes('loading');

  // Site rows count by who acts, like any card.
  const active = [...m.siteRows, ...m.needsYou, ...m.nexsoss].filter((it) => it.status === 'active');
  const needsN = active.filter((it) => it.group === 'needs_you').length;
  const callN = active.filter((it) => it.group === 'nexsoss' && it.evidence?.pleaseCall).length;
  const withN =
    active.filter((it) => it.group === 'nexsoss' && !it.evidence?.pleaseCall).length + m.pickedUp.length;
  const parts: string[] = [];
  if (needsN > 0) parts.push(`${needsN} needs you`);
  if (callN > 0) parts.push(`${callN} call Nexsoss`);
  if (withN > 0) parts.push(`${withN} with Nexsoss`);
  // A passive row (maintenance) still names itself; a cleared one does not.
  const siteActive = m.siteRows.filter((it) => it.status === 'active');
  if (parts.length === 0 && siteActive.length > 0) parts.push(siteActive[0]!.title);
  const cantText = cantCheck.length > 0 ? `Can't check: ${cantCheck.join(', ')}` : null;

  if (input.fireActive) return { kind: 'fire', text: FIRE_VERDICT, cantCheck };
  if (input.feed.clamped) {
    // The Overview heading's words, day included (`feedHeadingWords`):
    // the two sit on one page and read one FeedHealth.
    const last =
      input.feed.lastUpdateAtMs !== null
        ? ` · last update ${dayClockText(input.feed.lastUpdateAtMs, input.clock, input.nowMs)}`
        : '';
    return { kind: 'reconnecting', text: `Reconnecting${last}`, cantCheck };
  }
  if (parts.length > 0) {
    return { kind: 'items', text: [...parts, ...(cantText ? [cantText] : [])].join(' · '), cantCheck };
  }
  if (cantText) return { kind: 'cant_check', text: cantText, cantCheck };
  // Nothing anyone is asked to do, but a card is held up over a car that
  // can't be read (A7): one nobody acts on -- a hold carded as Info --
  // counts for nothing above, and "All clear" over it would claim what
  // nobody can see. Not added beside cards that DO ask something: the
  // held card is counted there, and "No reading" takes over in 10 s.
  if (held.length > 0) return { kind: 'cant_check', text: `Can't check: ${held.join(', ')}`, cantCheck: [...held] };
  if (loading) return { kind: 'checking', text: 'Checking robots and lifts…', cantCheck };
  if (m.otherOpenAlerts) return { kind: 'other_alerts', text: m.otherOpenAlerts.text, cantCheck };
  // Nothing is up, but something only LOOKS fixed: "All clear" waits for
  // its dwell to confirm it (A4) -- read from the debounce, so a key no
  // card shows still withholds it.
  if (looksFixed) return { kind: 'clearing', text: LOOKS_FIXED, cantCheck };
  return { kind: 'all_clear', text: ALL_CLEAR, cantCheck };
}
